import { notionStepIssue, notionTriggerIssue } from './notion/notion.util';
import {
  PLAY_VALUE_KEY,
  PLAY_VALUES_MAX,
  WRITE_SKILL_ID,
  writeOptionsProblem,
} from '@/utils/write-skill.util';
import { callNotesReady, callTriggerReady } from './call-trigger';
import { dealTriggerIssue, isDealKind } from './config/deal-trigger.util';
import { sixsenseTriggerIssue } from './config/sixsense-trigger.util';
import { normalizeNotifyConfig } from './config/notify-config.util';
import { sheetTriggerIssue } from './config/sheet-trigger.util';
import {
  columnRenamesOf,
  missingSheetColumnRefs,
  renameTriggerFieldRefs,
} from './sheet-column-refs';
import {
  DOC_RECORD_STEPS,
  createGoogleDocIssue,
  googleDocRecordIssues,
  googleDocTriggerIssue,
  isGoogleDocTrigger,
} from './config/google-doc.util';
import { sendMailboxEmailIssue } from './config/mailbox-email.util';
import { sendLinkedInActionIssue } from './config/linkedin-action.util';
import {
  fitsHubSpotJobChangeSearch,
  jobChangeReportsReady,
  jobChangeValue,
  saveJobChangeConfig,
} from './config/job-change-config';
import {
  buildAgentCompanyFilters,
  buildAgentPeopleFilters,
  fundingFiltered,
} from './search-audience';
import { nodeArms, firstNodeArm, isFork } from './node-arms';
import { ERROR_ARM, errorPathIssues } from './node-retries';
import { approvalConfigError } from './config/approval-config.util';
import {
  crmWriteConfigError,
  isCrmWriteAction,
} from './config/crm-write-config';
import {
  advancedOwner,
  advancedOwnerIssues,
} from './config/sync-owner-config.util';
import { branchConditionsComplete, branchLeaves } from './branch-conditions';
import { v4 as uuidv4 } from 'uuid';
import { incomingWebhookReady } from './config/incoming-webhook.util';
import { sequencePickWords, sequenceSenderIssue } from './sequence-step.util';
import {
  attioChangeKind,
  attioOperatorName,
  attioSearchOperators,
  crmChangeConditionsIssue,
  CRM_CHANGE_OPERATORS,
  hasCrmChangeConditions,
} from './config/crm-change-conditions';
import {
  isAttioId,
  isCrmFieldName,
  isCrmProjectionField,
} from './config/crm-fields';

import type {
  CustomEdge,
  CustomGraph,
  CustomNode,
  CustomNodeConfig,
  CustomNodeType,
  AddToSequenceConfig,
  AgentTaskConfig,
  ExportConfig,
  NotionStepConfig,
  CreateGoogleDocConfig,
  SendMailboxEmailConfig,
  SendLinkedInActionConfig,
  CountPeopleConfig,
  BranchArm,
  BranchConfig,
  CleanlistTriggerConfig,
  CleanupObject,
  CrmActionConfig,
  EnrichConfig,
  FindPeopleConfig,
  GetCompanyConfig,
  CallNotesConfig,
  GraphIssue,
  MergeDuplicatesConfig,
  NotifyConfig,
  RecordKind,
  ResearchConfig,
  ReviewDuplicatesConfig,
  TriggerConfig,
  TriggerSchedule,
  ValidateConfig,
  WebhookConfig,
  FindMeetingsConfig,
  JobHistoryConfig,
  IdentifyPersonConfig,
  MatchCompanyConfig,
  WaitConfig,
  CodeConfig,
} from '@/types/custom-playbooks';
import { triggerKind } from '@/types/custom-playbooks';
import { briefScheduleReady } from '@/containers/agents/brief.util';
import type { FilterGroup } from '@/types/filters';
import { INITIAL_FILTERS } from '@/types/search-filters';
import { buildCrustdataFilters } from '@/containers/search/people-search-hook';
import { countPeopleIssue } from './config/count-people.util';
import {
  buildCompanyFilters,
  hasAnyCompanyFilter,
  INITIAL_COMPANY_FILTERS,
} from '@/utils/company-search.utils';
import {
  isKnownNodeType,
  NODE_METADATA,
  NODE_TYPE_ORDER,
  nodeDefaultConfig,
} from './node-metadata';
import { isAdvisoryIssue } from './advisory-issues';
import { perPeriodIssues } from './per-period';
import { POLL_MINUTES } from './poll-interval';
import type { ConnectorPreset } from './connectors';
import { recordWindowError } from './trigger-record-windows';
import {
  companySignalIssue,
  signalDomains,
} from './config/company-signal.util';
import { waitConfigError } from './config/wait-config.util';
import { webhookConfigError } from './config/webhook-config.util';
import { codeConfigError } from './config/code-config.util';
import { findMeetingsConfigError } from './config/find-meetings.util';
import { jobHistoryConfigError } from './config/job-history.util';
import { identifyPersonConfigError } from './config/identify-person.util';
import { matchCompanyConfigError } from './config/match-company.util';
import {
  handOffConfigError,
  handOffTriggerReady,
} from './config/handoff-config.util';
import {
  CLEANUP_LINE,
  cleanupCrmType,
  cleanupStepFits,
  cleanupTriggerIssues,
  isCleanupStep,
  isCleanupTrigger,
  mergeConfigFor,
  mergeIssues,
  misplacedRuleIssues,
  reviewIssues,
} from './cleanup-rules';

/**
 * Pure graph operations for the Custom Playbook Builder. Every op takes a
 * graph and returns a NEW graph — no mutation, no React, no layout. Callers
 * re-run `layoutGraph` on the result.
 *
 * Validation mirrors the BE's errors-vs-warnings split (build-v2-plan §6.1):
 * structural ERRORS block Save; incompleteness WARNINGS persist with the draft
 * but block the On toggle and Run Test. Unwired branch arms are the one
 * warning that blocks NOTHING (§10.1-a) — a missing arm edge is legal runtime
 * semantics ("records matching this arm are dropped").
 */

export type IdGen = () => string;

const EMPTY_GROUP: FilterGroup = { operator: 'AND', conditions: [] };

const MAX_NODES = 50;
const MAX_BRANCH_DEPTH = 10;
const MIN_ARMS = 2;
const MAX_ARMS = 6;

export interface GraphValidationResult {
  ok: boolean;
  /** Structural — reject the save (BE mirrors). */
  errors: GraphIssue[];
  /** Draft-tolerant — persist, but block On / Run Test (except `unwired_arm`,
   *  which blocks nothing). */
  warnings: GraphIssue[];
}

/** Types renamed since older playbooks were saved — migrated in place so the
 *  user's intent is preserved. */
const NODE_TYPE_RENAMES: Record<string, CustomNodeType> = {
  find_committee: 'find_people',
};

/** Types a removed feature left in older saved plays. Nothing runs them any
 *  more, so `sanitizeGraph` splices them out. Only these: a type this build
 *  has never heard of is a step the API added since, and stays. */
const REMOVED_NODE_TYPES: ReadonlySet<string> = new Set([
  'ai_column',
  'icp_score',
  'delay',
  'filter',
  'export_csv',
]);

/**
 * The step types in `graph` this build doesn't know, once in order: steps the
 * API added after this portal was built (a Wait, an Approval). The builder
 * opens such a play read only, so a save can never drop the user's step.
 */
export function unknownStepTypes(graph: CustomGraph): string[] {
  const out: string[] = [];
  for (const node of graph.nodes) {
    const type = node.type as string;
    if (!isKnownNodeType(type) && !out.includes(type)) out.push(type);
  }
  return out;
}

function withoutCompanyFields(node: CustomNode): CustomNode {
  if (node.type !== 'enrich' || !('companyFields' in node.config)) return node;
  const config = { ...node.config };
  delete config.companyFields;
  return { ...node, config };
}

/**
 * Bring a persisted graph up to the current node vocabulary before it is ever
 * laid out or rendered: rename renamed types, then splice out the nodes of a
 * removed feature (ai_column/icp_score/delay/filter/export_csv). Any other
 * type this build doesn't know is kept as it is: the step is newer than the
 * portal, and splicing it would delete it on the next save
 * (play-builder waves R 1.12).
 */
export function sanitizeGraph(graph: CustomGraph): CustomGraph {
  let g: CustomGraph = {
    ...graph,
    nodes: graph.nodes.map((n) => {
      const renamed = NODE_TYPE_RENAMES[n.type as string];
      return withoutCompanyFields(renamed ? { ...n, type: renamed } : n);
    }),
  };
  // Removed features were all single-child linear steps, so
  // `deleteNodeAndSplice` reconnects cleanly.
  for (let guard = 0; guard < g.nodes.length + 1; guard += 1) {
    const stale = g.nodes.find((n) => REMOVED_NODE_TYPES.has(n.type as string));
    if (!stale) break;
    g = deleteNodeAndSplice(g, stale.id);
  }
  return g;
}

/**
 * Derive the Crustdata wire payloads for Cleanlist triggers AND Find People
 * nodes at save time (build-v3 §2b S1 — the `audience_wire_filters`
 * precedent). Pure: returns a NEW graph with
 * `cleanlist.peopleWireFilters` / `.companyWireFilters` (trigger) and
 * `audienceWireFilters` (find_people) set from the raw UI blobs via the
 * SINGLE converters (`buildCrustdataFilters` / `buildCompanyFilters`); the
 * raw blobs are kept verbatim for re-editing. Stale wire fields are dropped
 * when their raw blob is absent, so the BE can never consume a wire payload
 * the user has since cleared. Callers wire this into `serialize()` in
 * `custom-builder-container.component.tsx` so every persisted graph carries
 * the wire shape the poll/workflow consumes.
 *
 * Only an agent play compiles its trigger through the agent converters and
 * drops a wire that fails to compile; a library play keeps the shared
 * converters, and a compile error fails its save as it always has.
 */
export function withDerivedTriggerWire(
  graph: CustomGraph,
  agentPlay = false
): CustomGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const n = withoutCompanyFields(node);
      if (n.type === 'find_people' || n.type === 'count_people') {
        // Same S1 rule as the trigger: the BE (custom_playbook workflow)
        // consumes ONLY the pre-built Crustdata wire — never the raw
        // SearchFilters blob (finding #3). Count people takes Find People's
        // audience and reads its wire the same way.
        const cfg = n.config as Partial<FindPeopleConfig | CountPeopleConfig>;
        if (!cfg.audienceFilters) {
          if (cfg.audienceWireFilters === undefined) return n;
          const next = { ...cfg };
          delete next.audienceWireFilters;
          return { ...n, config: next as CustomNode['config'] };
        }
        let audienceWireFilters: ReturnType<typeof buildCrustdataFilters>;
        try {
          audienceWireFilters = buildCrustdataFilters(cfg.audienceFilters);
        } catch (error) {
          // Find People fails its save as it always has. Count people saves
          // no wire, so a bad saved query leaves the builder open and the
          // step warning (countPeopleIssue) names the error.
          if (n.type !== 'count_people') throw error;
          const next = { ...cfg };
          delete next.audienceWireFilters;
          return { ...n, config: next as CustomNode['config'] };
        }
        return {
          ...n,
          config: { ...cfg, audienceWireFilters } as CustomNode['config'],
        };
      }
      if (n.type !== 'trigger') return n;
      const cfg = n.config as Partial<TriggerConfig>;
      if (triggerKind(cfg) === 'job_change')
        return {
          ...n,
          config: saveJobChangeConfig(
            cfg as TriggerConfig,
            jobChangeValue(cfg)
          ),
        };
      const signal = cfg.companySignal;
      if (cfg.source === 'company_signal' && signal?.accounts)
        return {
          ...n,
          config: {
            ...cfg,
            companySignal: {
              ...signal,
              accounts: {
                ...signal.accounts,
                domains: signalDomains(signal.accounts.domains ?? []),
              },
            },
          } as TriggerConfig,
        };
      if (cfg.source !== 'cleanlist' || !cfg.cleanlist) return n;
      const next: CleanlistTriggerConfig = { ...cfg.cleanlist };
      if (next.kind === 'posts') {
        // The native post compiler owns the final ID/ICP intersection.
        delete next.peopleWireFilters;
        delete next.companyWireFilters;
        return { ...n, config: { ...cfg, cleanlist: next } as TriggerConfig };
      }
      if (next.peopleFilters) {
        try {
          next.peopleWireFilters = agentPlay
            ? buildAgentPeopleFilters(next.peopleFilters)
            : buildCrustdataFilters(next.peopleFilters);
        } catch (error) {
          if (!agentPlay) throw error;
          delete next.peopleWireFilters;
        }
      } else {
        delete next.peopleWireFilters;
      }
      // An empty company LinkedIn list is left out of the saved blob: an API
      // that predates the key refuses it ("Linkedin urls isn't a known
      // setting"), so a portal deployed first, or an API rolled back, would
      // block every company trigger saved meanwhile.
      if (
        next.companyFilters &&
        Array.isArray(next.companyFilters.linkedinUrls) &&
        next.companyFilters.linkedinUrls.length === 0
      ) {
        const companyFilters: Record<string, unknown> = {
          ...next.companyFilters,
        };
        delete companyFilters.linkedinUrls;
        next.companyFilters =
          companyFilters as unknown as typeof next.companyFilters;
      }
      if (next.companyFilters) {
        try {
          next.companyWireFilters = agentPlay
            ? buildAgentCompanyFilters(
                next.companyFilters,
                next.refireOnNewSignal === true
              )
            : buildCompanyFilters(next.companyFilters);
        } catch (error) {
          if (!agentPlay) throw error;
          delete next.companyWireFilters;
        }
      } else {
        delete next.companyWireFilters;
      }
      return { ...n, config: { ...cfg, cleanlist: next } as TriggerConfig };
    }),
  };
}

// ---------------------------------------------------------------------------
// Small internal helpers
// ---------------------------------------------------------------------------

function outEdges(graph: CustomGraph, nodeId: string): CustomEdge[] {
  return graph.edges.filter((e) => e.source === nodeId);
}

function inEdge(graph: CustomGraph, nodeId: string): CustomEdge | undefined {
  return graph.edges.find((e) => e.target === nodeId);
}

export { ERROR_ARM };

/** The step's out-edges that are not its error connection. */
function plainOutEdges(graph: CustomGraph, nodeId: string): CustomEdge[] {
  return outEdges(graph, nodeId).filter((e) => e.branchKey !== ERROR_ARM);
}

/** Every step above `nodeId` (excludes `nodeId` itself). Cycle-safe. */
export function ancestors(graph: CustomGraph, nodeId: string): string[] {
  const seen = new Set<string>();
  const stack = graph.edges
    .filter((e) => e.target === nodeId)
    .map((e) => e.source);
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of graph.edges) if (e.target === id) stack.push(e.source);
  }
  return [...seen];
}

/**
 * The steps every path to which runs through `nodeId` (G28): the fixpoint of
 * steps whose every parent is `nodeId` or already in. Sorted.
 */
export function exclusiveDescendants(
  graph: CustomGraph,
  nodeId: string
): string[] {
  const below = descendants(graph, nodeId).filter((id) => id !== nodeId);
  const found = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of below) {
      if (found.has(id)) continue;
      const parents = graph.edges
        .filter((e) => e.target === id)
        .map((e) => e.source);
      if (
        parents.length > 0 &&
        parents.every((p) => p === nodeId || found.has(p))
      ) {
        found.add(id);
        changed = true;
      }
    }
  }
  return [...found].sort();
}

function isBranch(node: CustomNode): boolean {
  return node.type === 'branch';
}

/** Every node reachable below `nodeId` (excludes `nodeId` itself). Cycle-safe. */
export function descendants(graph: CustomGraph, nodeId: string): string[] {
  const seen = new Set<string>();
  const stack = outEdges(graph, nodeId).map((e) => e.target);
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of outEdges(graph, id)) stack.push(e.target);
  }
  return [...seen];
}

/**
 * The objects a cleanup trigger checks. The trigger is the only list: a new
 * one starts from the org's CRM settings (Leads only for a Lead org), and one
 * that checks nothing gives a Merge step no object to guess at.
 */
function cleanupObjectsOf(graph: CustomGraph): CleanupObject[] {
  const config = graph.nodes.find((node) => node.type === 'trigger')?.config as
    | Partial<TriggerConfig>
    | undefined;
  return Array.isArray(config?.objects) ? [...config.objects] : [];
}

function makeNode(
  type: CustomNodeType,
  genId: IdGen,
  graph?: CustomGraph,
  sourceNodeId?: string,
  preset?: ConnectorPreset
): CustomNode {
  const trigger = graph?.nodes.find((node) => node.type === 'trigger')
    ?.config as TriggerConfig | undefined;
  let config =
    type === 'merge_duplicates' && graph
      ? mergeConfigFor(cleanupObjectsOf(graph))
      : nodeDefaultConfig(type);
  if (
    type === 'crm_action' &&
    trigger &&
    triggerKind(trigger) === 'job_change' &&
    graph &&
    sourceNodeId &&
    recordOutputKindAt(graph, sourceNodeId) === 'person'
  )
    config = {
      ...(config as CrmActionConfig),
      crmType: jobChangeValue(trigger).crmType,
      objectType: 'Contact',
      target: 'new_record',
    };
  // After the job change override, so its `target` survives a CRM row.
  if (preset)
    config = { ...config, ...structuredClone(preset) } as CustomNodeConfig;
  if (type === 'notify') config = normalizeNotifyConfig(config as NotifyConfig);
  return {
    id: genId(),
    type,
    config,
    // Placeholder — the caller re-runs layout, which overwrites this.
    position: { x: 0, y: 0 },
  };
}

// ---------------------------------------------------------------------------
// Branch arm key derivation
// ---------------------------------------------------------------------------

/**
 * Re-key an ordered arm list positionally and return the old→new key remap.
 * Condition arms (in order) become `b1`, `b2`, …; the single trailing else arm
 * (`conditions === null`) becomes `else`. Multiple else arms collapse to one
 * (defensive — should never happen). New arms carry a temp key that matches no
 * existing edge, so they simply have no remap entry.
 */
function deriveArmKeys(arms: BranchArm[]): {
  arms: BranchArm[];
  remap: Map<string, string>;
} {
  const conditionArms = arms.filter((a) => a.conditions !== null);
  const elseArm = arms.find((a) => a.conditions === null);

  const next: BranchArm[] = [];
  const remap = new Map<string, string>();

  conditionArms.forEach((arm, i) => {
    const key = `b${i + 1}`;
    remap.set(arm.key, key);
    next.push({ ...arm, key });
  });
  if (elseArm) {
    remap.set(elseArm.key, 'else');
    next.push({ ...elseArm, key: 'else', conditions: null });
  }
  return { arms: next, remap };
}

/**
 * Apply a new (already-ordered) arm list to a branch node: re-key positionally,
 * rewrite the branch's out-edges via the remap (dropping edges — and their
 * subtrees — for removed arms), and reorder the surviving out-edges to arm
 * order. All other nodes/edges pass through untouched.
 */
function applyArms(
  graph: CustomGraph,
  branchNodeId: string,
  orderedArms: BranchArm[]
): CustomGraph {
  const { arms, remap } = deriveArmKeys(orderedArms);
  const survivingKeys = new Set(arms.map((a) => a.key));

  // Update the node config.
  const nodes = graph.nodes.map((n) =>
    n.id === branchNodeId
      ? { ...n, config: { ...(n.config as BranchConfig), branches: arms } }
      : n
  );

  // Split this branch's out-edges from the rest; remap + drop.
  const branchOut: CustomEdge[] = [];
  const otherEdges: CustomEdge[] = [];
  for (const e of graph.edges) {
    if (e.source === branchNodeId) branchOut.push(e);
    else otherEdges.push(e);
  }

  const keptBranchOut: CustomEdge[] = [];
  const droppedTargets: string[] = [];
  for (const e of branchOut) {
    const newKey =
      e.branchKey !== undefined ? remap.get(e.branchKey) : undefined;
    if (newKey && survivingKeys.has(newKey)) {
      keptBranchOut.push({ ...e, branchKey: newKey });
    } else {
      droppedTargets.push(e.target);
    }
  }

  // Reorder kept out-edges to arm order (deterministic branch_edges).
  const orderIndex = new Map(arms.map((a, i) => [a.key, i]));
  keptBranchOut.sort(
    (a, b) =>
      (orderIndex.get(a.branchKey ?? '') ?? 0) -
      (orderIndex.get(b.branchKey ?? '') ?? 0)
  );

  let result: CustomGraph = {
    ...graph,
    nodes,
    edges: [...otherEdges, ...keptBranchOut],
  };

  // Cascade-delete the subtrees under any dropped arm. A step another arm
  // still joins keeps its other connections (G28).
  for (const target of droppedTargets) {
    if (!inEdge(result, target)) result = pruneSubtree(result, target);
  }
  return result;
}

/** Remove a node and the steps only it leads to; a step another branch still
 *  joins stays, without the pruned connections (G28). */
function pruneSubtree(graph: CustomGraph, nodeId: string): CustomGraph {
  const toRemove = new Set<string>([
    nodeId,
    ...exclusiveDescendants(graph, nodeId),
  ]);
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => !toRemove.has(n.id)),
    edges: graph.edges.filter(
      (e) => !toRemove.has(e.source) && !toRemove.has(e.target)
    ),
  };
}

// ---------------------------------------------------------------------------
// Node structural ops
// ---------------------------------------------------------------------------

/**
 * Insert a new node of `type` onto an existing edge: `source → new → target`.
 * `preset` patches its starting config (a connector row's tool).
 * The upstream half keeps the original edge's `branchKey` (so inserting on a
 * branch arm stays on that arm). The new node's downstream edge is plain —
 * except when the inserted node is itself a Branch, whose out-edges MUST
 * carry an arm key: the pre-existing subtree lands on the default config's
 * first condition arm (`b1`), matching what a user forking mid-chain expects
 * (finding #19 — a keyless branch out-edge fails both validators and is
 * dropped by the layout).
 */
export function insertOnEdge(
  graph: CustomGraph,
  edgeId: string,
  type: CustomNodeType,
  genId: IdGen = uuidv4,
  preset?: ConnectorPreset
): CustomGraph {
  const edge = graph.edges.find((e) => e.id === edgeId);
  if (!edge) throw new Error(`insertOnEdge: no edge ${edgeId}`);
  const node = makeNode(type, genId, graph, edge.source, preset);
  const upstream: CustomEdge = {
    id: genId(),
    source: edge.source,
    target: node.id,
    ...(edge.branchKey !== undefined ? { branchKey: edge.branchKey } : {}),
  };
  const firstArmKey = firstNodeArm(node);
  const downstream: CustomEdge = {
    id: genId(),
    source: node.id,
    target: edge.target,
    ...(firstArmKey !== undefined ? { branchKey: firstArmKey } : {}),
  };
  return {
    ...graph,
    nodes: [...graph.nodes, node],
    edges: [
      ...graph.edges.filter((e) => e.id !== edgeId),
      upstream,
      downstream,
    ],
  };
}

/**
 * Append a new node of `type` as the child of `nodeId`. For a branch parent,
 * `branchKey` selects which arm the child attaches to (connecting an unconnected
 * arm stub). `preset` patches its starting config. Throws if the target slot
 * is already occupied (would break the single-child tree invariant).
 */
export function appendAfter(
  graph: CustomGraph,
  nodeId: string,
  type: CustomNodeType,
  opts: { branchKey?: string; genId?: IdGen; preset?: ConnectorPreset } = {}
): CustomGraph {
  const genId = opts.genId ?? uuidv4;
  const parent = graph.nodes.find((n) => n.id === nodeId);
  if (!parent) throw new Error(`appendAfter: no node ${nodeId}`);

  const existing = outEdges(graph, nodeId);
  if (opts.branchKey === ERROR_ARM && !isFork(parent)) {
    // G29: a step that routes failures adds its error path here.
    if (parent.onError?.then !== 'route')
      throw new Error('appendAfter: this step has no error path');
    if (existing.some((e) => e.branchKey === ERROR_ARM))
      throw new Error('appendAfter: the error path is already connected');
  } else if (opts.branchKey !== undefined) {
    if (
      !isFork(parent) ||
      !nodeArms(parent).some((arm) => arm.key === opts.branchKey)
    )
      throw new Error('appendAfter: choose an existing path');
    if (existing.some((e) => e.branchKey === opts.branchKey))
      throw new Error(`appendAfter: arm ${opts.branchKey} already connected`);
  } else if (isFork(parent)) {
    throw new Error('appendAfter: choose a path');
  } else if (existing.some((e) => e.branchKey !== ERROR_ARM)) {
    throw new Error('appendAfter: node already has a child');
  }

  const node = makeNode(type, genId, graph, nodeId, opts.preset);
  const edge: CustomEdge = {
    id: genId(),
    source: nodeId,
    target: node.id,
    ...(opts.branchKey !== undefined ? { branchKey: opts.branchKey } : {}),
  };
  return {
    ...graph,
    nodes: [...graph.nodes, node],
    edges: [...graph.edges, edge],
  };
}

/**
 * Delete a node, fixing up edges:
 * - linear node (≤1 child): splice its parent straight to its child, preserving
 *   the parent edge's `branchKey`;
 * - branch node (>1 child): remove the node and its entire subtree.
 * Throws when asked to delete the trigger/root.
 */
export function deleteNodeAndSplice(
  graph: CustomGraph,
  nodeId: string
): CustomGraph {
  const parentEdges = graph.edges.filter((e) => e.target === nodeId);
  if (parentEdges.length === 0)
    throw new Error('deleteNodeAndSplice: cannot delete the root');

  const node = graph.nodes.find((n) => n.id === nodeId);
  const children =
    node && isFork(node)
      ? outEdges(graph, nodeId)
      : plainOutEdges(graph, nodeId);
  if (children.length > 1) {
    // A fork can't be spliced 1:1 — drop the node and what only it leads to.
    return pruneSubtree(graph, nodeId);
  }
  // G29: the step's error path goes with it; a step it rejoins stays.
  const error =
    node && !isFork(node)
      ? outEdges(graph, nodeId).find((e) => e.branchKey === ERROR_ARM)
      : undefined;
  if (error) {
    graph = { ...graph, edges: graph.edges.filter((e) => e.id !== error.id) };
    if (!inEdge(graph, error.target)) graph = pruneSubtree(graph, error.target);
  }

  const child = children[0];
  const parentIds = new Set(parentEdges.map((e) => e.id));
  const remaining = graph.edges.filter(
    (e) => !parentIds.has(e.id) && (!child || e.id !== child.id)
  );
  // G28: every incoming connection now leads to the child.
  const splice: CustomEdge[] = child
    ? parentEdges.map((parentEdge) => ({
        id: parentEdge.id,
        source: parentEdge.source,
        target: child.target,
        ...(parentEdge.branchKey !== undefined
          ? { branchKey: parentEdge.branchKey }
          : {}),
      }))
    : [];
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => n.id !== nodeId),
    edges: [...remaining, ...splice],
  };
}

/**
 * G28: end `from` (or its arm) on `to`, a step another arm already leads to,
 * so records from every joined arm run it together. The slot must be empty;
 * `to` can't be the trigger or a step above `from`.
 */
export function connectStep(
  graph: CustomGraph,
  from: string,
  to: string,
  opts: { branchKey?: string; genId?: IdGen } = {}
): CustomGraph {
  const genId = opts.genId ?? uuidv4;
  const parent = graph.nodes.find((n) => n.id === from);
  const target = graph.nodes.find((n) => n.id === to);
  if (!parent || !target) throw new Error('connectStep: no such step');
  if (
    to === from ||
    target.type === 'trigger' ||
    ancestors(graph, from).includes(to)
  )
    throw new Error("connectStep: a play can't loop back on itself");
  const taken = outEdges(graph, from).some((e) =>
    opts.branchKey !== undefined
      ? e.branchKey === opts.branchKey
      : e.branchKey !== ERROR_ARM
  );
  if (taken) throw new Error('connectStep: that slot already leads to a step');
  if (opts.branchKey === undefined && isFork(parent))
    throw new Error('connectStep: choose a path');
  const edge: CustomEdge = {
    id: genId(),
    source: from,
    target: to,
    ...(opts.branchKey !== undefined ? { branchKey: opts.branchKey } : {}),
  };
  return { ...graph, edges: [...graph.edges, edge] };
}

/** G28: remove one connection into a joined step; its last one stays. */
export function disconnectStep(
  graph: CustomGraph,
  from: string,
  to: string,
  branchKey?: string
): CustomGraph {
  const match = graph.edges.find(
    (e) =>
      e.source === from &&
      e.target === to &&
      (branchKey === undefined || e.branchKey === branchKey)
  );
  if (!match) throw new Error('disconnectStep: no such connection');
  if (graph.edges.filter((e) => e.target === to).length < 2)
    throw new Error("disconnectStep: the step's only connection stays");
  return { ...graph, edges: graph.edges.filter((e) => e.id !== match.id) };
}

/**
 * The steps an open slot under `nodeId` (its arm `branchKey`) can join
 * (G28): not the trigger, not above it, not already its next step, and
 * taking the kind of record the slot sends.
 */
export function joinTargets(
  graph: CustomGraph,
  nodeId: string,
  branchKey?: string
): CustomNode[] {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) return [];
  const sends =
    branchKey === ERROR_ARM
      ? recordKindAt(graph, nodeId)
      : node.type === 'trigger'
        ? null
        : recordOutputKindAt(graph, nodeId);
  if (!sends) return [];
  const above = new Set(ancestors(graph, nodeId));
  return graph.nodes.filter(
    (target) =>
      target.id !== nodeId &&
      target.type !== 'trigger' &&
      !above.has(target.id) &&
      graph.edges.some((e) => e.target === target.id) &&
      recordKindAt(graph, target.id) === sends
  );
}

/** Replace a node's config. Branch nodes are routed through arm re-keying so
 *  the out-edge/arm invariant can never drift. */
export function setNodeConfig(
  graph: CustomGraph,
  nodeId: string,
  config: CustomNode['config']
): CustomGraph {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) throw new Error(`setNodeConfig: no node ${nodeId}`);
  if (node.type === 'notify')
    config = normalizeNotifyConfig(config as NotifyConfig);
  if (isBranch(node)) {
    const branches = (config as BranchConfig).branches ?? [];
    // Re-key + resync edges, then fold in any other config fields.
    const synced = applyArms(graph, nodeId, branches);
    return {
      ...synced,
      nodes: synced.nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              config: {
                ...config,
                branches: (n.config as BranchConfig).branches,
              },
            }
          : n
      ),
    };
  }
  const next = {
    ...graph,
    nodes: graph.nodes.map((n) => (n.id === nodeId ? { ...n, config } : n)),
  };
  // A sheet trigger's header edit that moved column keys carries the moves:
  // the later steps' `trigger.<key>` reads follow them.
  const renames = node.type === 'trigger' ? columnRenamesOf(config) : undefined;
  return renames ? renameTriggerFieldRefs(next, nodeId, renames) : next;
}

// ---------------------------------------------------------------------------
// Branch arm ops (all re-derive branchKeys from arm order)
// ---------------------------------------------------------------------------

/** Add a condition arm just before the trailing else arm (up to MAX_ARMS). */
export function addBranchArm(
  graph: CustomGraph,
  branchNodeId: string
): CustomGraph {
  const node = graph.nodes.find((n) => n.id === branchNodeId);
  if (!node || !isBranch(node)) throw new Error('addBranchArm: not a branch');
  const arms = (node.config as BranchConfig).branches;
  const conditionArms = arms.filter((a) => a.conditions !== null);
  if (conditionArms.length + 1 > MAX_ARMS - 1) {
    // +1 new condition arm, and the else arm — cap total at MAX_ARMS.
    return graph;
  }
  const elseArm = arms.find((a) => a.conditions === null);
  const newArm: BranchArm = {
    key: uuidv4(), // temp; deriveArmKeys re-keys positionally
    label: `Branch ${conditionArms.length + 1}`,
    conditions: { ...EMPTY_GROUP },
  };
  const ordered = [...conditionArms, newArm, ...(elseArm ? [elseArm] : [])];
  return applyArms(graph, branchNodeId, ordered);
}

/** Remove a condition arm (and its subtree) by key; keeps ≥ MIN_ARMS arms. */
export function removeArm(
  graph: CustomGraph,
  branchNodeId: string,
  armKey: string
): CustomGraph {
  const node = graph.nodes.find((n) => n.id === branchNodeId);
  if (!node || !isBranch(node)) throw new Error('removeArm: not a branch');
  const arms = (node.config as BranchConfig).branches;
  if (arms.length <= MIN_ARMS) return graph; // floor
  const target = arms.find((a) => a.key === armKey);
  if (!target || target.conditions === null) return graph; // never drop else
  const ordered = arms.filter((a) => a.key !== armKey);
  return applyArms(graph, branchNodeId, ordered);
}

/**
 * Reorder the condition arms of a branch (0-based indices among the CONDITION
 * arms; the else arm is always pinned last and cannot be moved). Re-derives
 * keys and remaps out-edges so each subtree stays attached to its arm.
 */
export function reorderArms(
  graph: CustomGraph,
  branchNodeId: string,
  fromIndex: number,
  toIndex: number
): CustomGraph {
  const node = graph.nodes.find((n) => n.id === branchNodeId);
  if (!node || !isBranch(node)) throw new Error('reorderArms: not a branch');
  const arms = (node.config as BranchConfig).branches;
  const conditionArms = arms.filter((a) => a.conditions !== null);
  const elseArm = arms.find((a) => a.conditions === null);
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= conditionArms.length ||
    toIndex >= conditionArms.length ||
    fromIndex === toIndex
  ) {
    return graph;
  }
  const reordered = [...conditionArms];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  const ordered = [...reordered, ...(elseArm ? [elseArm] : [])];
  return applyArms(graph, branchNodeId, ordered);
}

// ---------------------------------------------------------------------------
// Move, matching the server move_node contract
// ---------------------------------------------------------------------------

export class MoveNodeError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'MoveNodeError';
  }
}

export interface MoveSlot {
  after: string;
  arm?: string;
  edgeId?: string;
}

/** A fork carries its descendants; a linear step leaves its child behind. */
export function moveNode(
  graph: CustomGraph,
  nodeId: string,
  afterId: string,
  arm?: string,
  idGen: IdGen = uuidv4
): CustomGraph {
  const node = graph.nodes.find((n) => n.id === nodeId);
  const parent = graph.nodes.find((n) => n.id === afterId);
  if (!node)
    throw new MoveNodeError(
      'unknown_node',
      'The step is no longer in this play.'
    );
  if (node.type === 'trigger')
    throw new MoveNodeError('cannot_move_trigger', 'The trigger stays first.');
  if (!parent)
    throw new MoveNodeError(
      'unknown_node',
      'The destination is no longer in this play.'
    );
  const branching = isFork(node);
  const moving = branching ? [nodeId, ...descendants(graph, nodeId)] : [nodeId];
  if (moving.includes(afterId))
    throw new MoveNodeError(
      'move_into_itself',
      'A step cannot move into itself or its carried subtree.'
    );
  if (
    moving.some((id) => graph.edges.filter((e) => e.target === id).length !== 1)
  )
    throw new MoveNodeError(
      'move_join',
      'A joined or disconnected step cannot move.'
    );
  const oldParent = inEdge(graph, nodeId)!;
  const children = outEdges(graph, nodeId);
  if (!branching && children.length > 1)
    throw new MoveNodeError(
      'move_fanout',
      'This step has more than one child.'
    );
  if (arm !== undefined && !isFork(parent))
    throw new MoveNodeError('arm_not_branch', 'Choose a step with paths.');
  if (isFork(parent)) {
    if (arm === undefined)
      throw new MoveNodeError('arm_required', 'Choose a path.');
    if (!nodeArms(parent).some((a) => a.key === arm))
      throw new MoveNodeError('unknown_arm', 'The step has no such path.');
  }
  const slot = outEdges(graph, afterId).filter(
    (e) => arm === undefined || e.branchKey === arm
  );
  // Return the original object so a no-op does not create an undo entry.
  if (oldParent.source === afterId && oldParent.branchKey === arm) return graph;
  const firstArm = firstNodeArm(node);
  if (
    branching &&
    slot.length &&
    (firstArm === undefined || children.some((e) => e.branchKey === firstArm))
  )
    throw new MoveNodeError(
      'move_branch_occupied',
      'The moved step’s first path is occupied.'
    );
  const child = !branching ? children[0] : undefined;
  let edges = graph.edges.filter((e) => e !== oldParent && e !== child);
  if (child) edges = [...edges, { ...oldParent, target: child.target }];
  // Re-read the destination after splicing, as insert_after does on the server.
  const destination = edges.find(
    (e) => e.source === afterId && (arm === undefined || e.branchKey === arm)
  );
  if (destination) {
    const upstream: CustomEdge = {
      id: idGen(),
      source: afterId,
      target: nodeId,
    };
    if (destination.branchKey !== undefined)
      upstream.branchKey = destination.branchKey;
    const downstream: CustomEdge = {
      id: idGen(),
      source: nodeId,
      target: destination.target,
    };
    if (firstArm !== undefined) downstream.branchKey = firstArm;
    edges = [...edges.filter((e) => e !== destination), upstream, downstream];
  } else {
    const edge: CustomEdge = { id: idGen(), source: afterId, target: nodeId };
    if (arm !== undefined) edge.branchKey = arm;
    edges = [...edges, edge];
  }
  return {
    ...graph,
    nodes: [...graph.nodes.filter((n) => n !== node), node],
    edges,
  };
}

/** Canvas destinations use the same pure mutation and normal structural/kind
 * validator. Draft warnings stay visible through the existing Save flow. */
export function moveSlots(graph: CustomGraph, nodeId: string): MoveSlot[] {
  const slots: MoveSlot[] = [];
  for (const parent of graph.nodes) {
    const arms = isFork(parent)
      ? nodeArms(parent).map((a) => a.key)
      : [undefined];
    for (const arm of arms) {
      try {
        let nextId = 0;
        const usedIds = new Set(
          [...graph.nodes, ...graph.edges].map((item) => item.id)
        );
        const candidate = moveNode(graph, nodeId, parent.id, arm, () => {
          let id = `move-preview-${nextId++}`;
          while (usedIds.has(id)) id = `move-preview-${nextId++}`;
          usedIds.add(id);
          return id;
        });
        if (candidate === graph || validateGraph(candidate).errors.length)
          continue;
        const edge = outEdges(graph, parent.id).find(
          (e) => arm === undefined || e.branchKey === arm
        );
        slots.push({
          after: parent.id,
          ...(arm !== undefined ? { arm } : {}),
          ...(edge ? { edgeId: edge.id } : {}),
        });
      } catch (error) {
        if (!(error instanceof MoveNodeError)) throw error;
      }
    }
  }
  return slots;
}

// ---------------------------------------------------------------------------
// Undo / redo history (bounded snapshot stack — build-v2-plan §3.3)
// ---------------------------------------------------------------------------

export const HISTORY_CAP = 50;

export interface GraphHistory {
  past: CustomGraph[];
  future: CustomGraph[];
}

export const EMPTY_HISTORY: GraphHistory = { past: [], future: [] };

/** Record `snapshot` (the pre-mutation graph) after a fresh edit; a new edit
 *  always clears the redo stack. Bounded at HISTORY_CAP snapshots. */
export function pushHistory(
  h: GraphHistory,
  snapshot: CustomGraph
): GraphHistory {
  const past = [...h.past, snapshot];
  if (past.length > HISTORY_CAP) past.shift();
  return { past, future: [] };
}

/** Step back: returns the restored graph, or null when there is nothing to
 *  undo. `current` moves onto the redo stack. */
export function undoHistory(
  h: GraphHistory,
  current: CustomGraph
): { history: GraphHistory; graph: CustomGraph } | null {
  if (h.past.length === 0) return null;
  const graph = h.past[h.past.length - 1];
  return {
    history: { past: h.past.slice(0, -1), future: [...h.future, current] },
    graph,
  };
}

/** Step forward after an undo. */
export function redoHistory(
  h: GraphHistory,
  current: CustomGraph
): { history: GraphHistory; graph: CustomGraph } | null {
  if (h.future.length === 0) return null;
  const graph = h.future[h.future.length - 1];
  return {
    history: { past: [...h.past, current], future: h.future.slice(0, -1) },
    graph,
  };
}

// ---------------------------------------------------------------------------
// Config completeness (drives trigger gating + On-toggle warnings)
// ---------------------------------------------------------------------------

/** Operators that need no value (parity with the shared filter builders'
 *  `isFilterConditionComplete` — kept local so this module stays React-free). */
const VALUELESS_OPERATORS = new Set([
  'exists',
  'not_exists',
  'known',
  'unknown',
  'is_known',
  'is_unknown',
]);

interface ConditionLike {
  field?: string;
  operator?: string;
  value?: unknown;
}

function isConditionComplete(c: ConditionLike): boolean {
  if (!c.field || !String(c.field).trim()) return false;
  if (c.operator && VALUELESS_OPERATORS.has(c.operator)) return true;
  if (typeof c.value === 'boolean') return true;
  if (c.value === undefined || c.value === null) return false;
  if (Array.isArray(c.value)) return c.value.length > 0;
  return String(c.value).trim() !== '';
}

function groupComplete(group: FilterGroup | null | undefined): boolean {
  if (!group || group.conditions.length === 0) return false;
  return group.conditions.every((c) => isConditionComplete(c as ConditionLike));
}

function isDefaultFilters(value: unknown, initial: unknown): boolean {
  return JSON.stringify(value ?? initial) === JSON.stringify(initial);
}

// ---------------------------------------------------------------------------
// Attio: the API's save checks for Attio triggers and steps
// (custom_playbook_graph._v_trigger and _v_crm_action, attio_filters,
// crm_change_trigger.change_plan), so the builder refuses what Save refuses.
// ---------------------------------------------------------------------------

const ATTIO_NUMBER = /^-?[0-9]+(\.[0-9]+)?$/;
const ATTIO_DAY = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const ATTIO_INSTANT =
  /^[0-9]{4}-[0-9]{2}-[0-9]{2}[T ][0-9]{2}:[0-9]{2}(?::[0-9]{2}(?:\.[0-9]{1,9})?)?(?:Z|[+-][0-9]{2}:?[0-9]{2})?$/;
const ATTIO_NO_OWNER =
  'Attio people and companies have no owner, so a step can’t check who owns them on Attio.';

function attioRealDay(text: string): boolean {
  if (!ATTIO_DAY.test(text)) return false;
  const [y, m, d] = text.split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d));
  return day.getUTCMonth() === m - 1 && day.getUTCDate() === d;
}

/** One condition value as Attio's search takes it (attio_filters._one_value
 *  and the timestamp leaf). */
function attioValueOk(kind: string, value: unknown): boolean {
  if (value === null || value === undefined || typeof value === 'object')
    return false;
  if (kind === 'checkbox')
    return (
      typeof value === 'boolean' ||
      ['true', 'false'].includes(String(value).trim().toLowerCase())
    );
  if (typeof value === 'boolean') return false;
  const text = String(value).trim();
  if (!text) return false;
  if (kind === 'number' || kind === 'currency')
    return typeof value === 'number'
      ? Number.isFinite(value)
      : ATTIO_NUMBER.test(text);
  if (kind === 'date') return attioRealDay(text);
  if (kind === 'timestamp')
    return ATTIO_INSTANT.test(text) || attioRealDay(text);
  if (kind === 'select' || kind === 'status')
    // Compared by the option's or status's id, so a rename still matches.
    return /^[0-9a-f]{32}$/i.test(
      text
        .replace(/^urn:uuid:/i, '')
        .replace(/^\{|\}$/g, '')
        .replace(/-/g, '')
    );
  return true;
}

/** Why Attio's search refuses one saved condition, numbered as saved, in the
 *  API's words (condition_refusal_sentence), or null. */
function attioConditionIssue(condition: unknown, index: number): string | null {
  const c = (condition ?? {}) as ConditionLike & { fieldType?: unknown };
  const field = typeof c.field === 'string' ? c.field.trim() : '';
  const rawOperator = typeof c.operator === 'string' ? c.operator.trim() : '';
  if (!field || !rawOperator) return `Condition ${index} is incomplete.`;
  const subject = `Condition ${index} (${field})`;
  const kind =
    typeof c.fieldType === 'string' ? c.fieldType.trim().toLowerCase() : '';
  const allowed = attioSearchOperators(kind);
  // A parent field (company.x) is refused too: the search reads the record's
  // own attributes only.
  if (!isCrmFieldName(field, 'attio') || !allowed)
    return `${subject} uses a field Attio can’t search.`;
  const operator = attioOperatorName(rawOperator);
  if (!allowed.includes(operator))
    return `${subject} uses an operator Attio can’t search.`;
  if (operator === 'exists' || operator === 'not_exists') return null;
  const values =
    (operator === 'in' || operator === 'not_in') && Array.isArray(c.value)
      ? c.value
      : [c.value];
  return values.length > 0 && values.every((value) => attioValueOk(kind, value))
    ? null
    : `${subject} has a value Attio can’t search.`;
}

/** Why the API refuses an Attio CRM trigger (record matches, field changes
 *  or list joins), in its words, or null. Deal triggers are checked by
 *  `dealTriggerIssue`. */
export function attioTriggerIssue(
  crm: NonNullable<TriggerConfig['crm']>
): string | null {
  const objectType = crm.objectType as string | undefined;
  if (objectType === 'Lead') return 'Attio has no Leads. Use Contact (people).';
  const peopleOrCompanies =
    objectType === 'Contact' || objectType === 'Account';
  if (crm.conditionType === 'list_membership') {
    if (!peopleOrCompanies)
      return 'Attio list triggers watch lists of people or companies.';
  } else if (!peopleOrCompanies) return 'Pick an object this CRM supports.';
  const fields: unknown = crm.fields;
  if (fields !== undefined && fields !== null) {
    if (!Array.isArray(fields) || fields.length > 20)
      return 'Carry at most 20 Attio attributes.';
    if (
      !fields.every(
        (name) =>
          typeof name === 'string' &&
          isCrmProjectionField(name, 'attio', objectType as string)
      )
    )
      return 'Pick Attio attributes to carry.';
    if (new Set(fields).size !== fields.length)
      return 'Carry each Attio attribute once.';
  }
  if (crm.conditionType === 'list_membership') {
    const listId = String(crm.listId ?? '').trim();
    if (!listId) return 'Pick a list to watch.';
    // The Attio lists picker gives the list's id, never its slug.
    return isAttioId(String(crm.listId))
      ? null
      : 'Pick an Attio list to watch.';
  }
  if (crm.conditionType !== 'property')
    return 'Pick a property or list condition.';
  const group = crm.conditions as
    | { operator?: unknown; conditions?: unknown }
    | null
    | undefined;
  const conditions = Array.isArray(group?.conditions) ? group.conditions : [];
  if (!conditions.length) return 'Complete the trigger conditions.';
  // The API reads a group with no operator as AND, in any case.
  const joiner =
    group?.operator === undefined
      ? 'AND'
      : typeof group.operator === 'string'
        ? group.operator.toUpperCase()
        : '';
  const changes = hasCrmChangeConditions(group);
  if (changes) {
    if (
      joiner !== 'AND' ||
      conditions.some(
        (c) => !c || typeof c !== 'object' || 'conditions' in (c as object)
      )
    )
      return 'Changes work in an AND group with no nested groups.';
    if (conditions.length > 20) return 'Use 20 conditions or fewer.';
    const watched = new Map<string, string>();
    let count = 0;
    for (const raw of conditions) {
      const c = raw as ConditionLike & { fieldType?: unknown };
      if (typeof c.field !== 'string' || typeof c.operator !== 'string')
        return 'Complete every condition.';
      if (!CRM_CHANGE_OPERATORS.has(c.operator)) continue;
      count += 1;
      if (!isCrmFieldName(c.field, 'attio'))
        return 'Use the record’s own fields for changes.';
      const kind =
        typeof c.fieldType === 'string'
          ? attioChangeKind(c.fieldType)
          : c.fieldType === undefined
            ? 'text'
            : undefined;
      if (!kind) return 'Changes can’t watch this Attio attribute type.';
      if ((watched.get(c.field) ?? kind) !== kind)
        return 'Watch each field as one type.';
      watched.set(c.field, kind);
      if (
        c.operator !== 'changed' &&
        !Object.prototype.hasOwnProperty.call(c, 'value')
      )
        return 'Pick the value the field changes to or from.';
    }
    if (count > 3) return 'Up to 3 change conditions.';
  } else if (joiner !== 'AND' && joiner !== 'OR') {
    return 'Match all or any of the conditions.';
  }
  for (const [i, condition] of conditions.entries()) {
    const operator = (condition as ConditionLike | null)?.operator;
    if (changes && operator && CRM_CHANGE_OPERATORS.has(operator)) continue;
    const issue = attioConditionIssue(condition, i + 1);
    if (issue) return issue;
  }
  return null;
}

/** Why the API refuses an Attio Look up or Sync, in its words, or null
 *  (attio_lookup_refusals and attio_sync_refusals). Writes are checked by
 *  `crmWriteConfigError`. */
export function attioCrmStepIssue(c: CrmActionConfig): string | null {
  if (c.crmType !== 'attio') return null;
  if (c.action === 'lookup') {
    if (!['Contact', 'Account', 'default'].includes(c.objectType as string))
      return 'Attio has no Leads. Look up a Contact (people) or an Account (companies).';
    const lookup = c.lookupConfig;
    if (
      Object.values(lookup?.options ?? {}).some(
        (option) =>
          !!option &&
          typeof option === 'object' &&
          (option as { enabled?: unknown }).enabled === true
      )
    )
      return 'Attio has no Pull back options. Carry the Attio fields you need instead.';
    const related = lookup?.related ?? {};
    if ('activities' in related)
      return 'Attio activity isn’t read, so leave out related activities.';
    if ('notes' in related)
      return 'Attio notes aren’t read by Look up, so leave out related notes.';
    if ('contacts' in related)
      return 'Attio company contacts aren’t read, so leave out related contacts.';
    return null;
  }
  if (c.action !== 'sync') return null;
  if (c.objectType === 'Lead')
    return 'Attio has no Leads. Sync a Contact (people) or an Account (companies).';
  if (c.target === 'new_record')
    return 'An Attio Sync always matches first, so a new record isn’t available on Attio.';
  if (c.assignment && c.assignment.mode !== 'keep') return ATTIO_NO_OWNER;
  if (c.syncProfileId) return 'Sync profiles aren’t available on Attio.';
  return null;
}

/** The API's words for a companies list on a library play: the poll reads
 *  one only on an agent's play (`agent_play_only`). */
const ATTIO_COMPANY_LIST_AGENT_ONLY =
  'Company CRM lists run only on an agent’s play. Watch a list of people.';

function attioCompanyListOnLibrary(
  crm: NonNullable<TriggerConfig['crm']>,
  agentPlay: boolean
): boolean {
  return (
    !agentPlay &&
    crm.conditionType === 'list_membership' &&
    crm.objectType === 'Account'
  );
}

/** The Attio words for a CRM trigger that isn't ready, or null when the
 *  trigger isn't a plain Attio CRM trigger. */
function attioTriggerWords(
  c: Partial<TriggerConfig>,
  agentPlay: boolean
): string | null {
  const kind = triggerKind(c);
  if (
    c.source !== 'crm' ||
    c.crm?.crmType !== 'attio' ||
    isDealKind(kind) ||
    kind === 'sixsense_stage' ||
    kind === 'job_change'
  )
    return null;
  return (
    attioTriggerIssue(c.crm) ??
    (attioCompanyListOnLibrary(c.crm, agentPlay)
      ? ATTIO_COMPANY_LIST_AGENT_ONLY
      : null)
  );
}

/**
 * Trigger readiness (§3.1-6): a source is chosen AND its config is complete.
 * Until this returns true, nothing else can be added to the canvas.
 */
export function validateTriggerConfig(
  config: TriggerConfig | undefined,
  agentPlay = false
): boolean {
  const c = config as Partial<TriggerConfig> | undefined;
  if (!c?.source) return false;
  if (isDealKind(triggerKind(c)))
    return agentPlay && dealTriggerIssue(c) === null;
  if (triggerKind(c) === 'sixsense_stage')
    return agentPlay && sixsenseTriggerIssue(c) === null;
  if (c.source === 'company_signal')
    return agentPlay && companySignalIssue(c.companySignal) === null;
  if (c.source === 'fireflies' || c.source === 'fathom' || c.source === 'gong')
    return (
      agentPlay &&
      c.kind === `${c.source}_call` &&
      callTriggerReady(c[c.source])
    );
  if (c.source === 'google_sheet') return sheetTriggerIssue(c.sheet) === null;
  if (c.source === 'notion')
    return agentPlay && notionTriggerIssue(c.notion) === null;
  if (c.source === 'google_doc')
    return (
      c.kind === 'google_doc_updated' &&
      googleDocTriggerIssue(c.googleDoc) === null
    );
  if (c.source === 'calendar') {
    const filters = c.calendar;
    return (
      !!filters &&
      agentPlay &&
      (!c.schedule || c.schedule.frequency === 'continuous') &&
      Array.isArray(filters.googleUserIds) &&
      filters.googleUserIds.length >= 1 &&
      filters.googleUserIds.length <= 20 &&
      new Set(filters.googleUserIds).size === filters.googleUserIds.length &&
      filters.googleUserIds.every(
        (member) =>
          typeof member === 'string' &&
          !!member &&
          member.trim() === member &&
          member.length <= 255
      ) &&
      c.kind === `meeting_${filters.event}` &&
      ['booked', 'starting', 'no_notes'].includes(filters.event) &&
      filters.who === 'external_attendees' &&
      Number.isInteger(filters.maxPerMeeting) &&
      filters.maxPerMeeting >= 1 &&
      filters.maxPerMeeting <= 10 &&
      (filters.event === 'starting'
        ? Number.isInteger(filters.hoursBefore) &&
          (filters.hoursBefore ?? 0) >= 1 &&
          (filters.hoursBefore ?? 0) <= 72
        : filters.hoursBefore == null) &&
      Array.isArray(filters.titleContains) &&
      filters.titleContains.length <= 10 &&
      filters.titleContains.every(
        (word) =>
          typeof word === 'string' && !!word.trim() && word.trim().length <= 60
      )
    );
  }
  if (c.source === 'sequence') return false;
  if (c.source === 'webhook') return incomingWebhookReady(c.webhook);
  if (c.source === 'play') return handOffTriggerReady(c);
  // A scheduled brief (agent's play only): Once with kind one_time, any
  // other cadence with kind schedule, at a slot it can run (the API's
  // trigger_kind, trigger_schedule_task_frequency and _v_schedule).
  if (c.source === 'schedule') {
    const kind = c.kind as string | undefined;
    return (
      agentPlay &&
      !!c.schedule &&
      (kind === 'schedule' || kind === 'one_time') &&
      (kind === 'one_time') === (c.schedule.frequency === 'once') &&
      briefScheduleReady(c.schedule)
    );
  }
  // A cleanup trigger is ready once it knows its CRM, and a list of objects
  // it holds names one at least. With no list it checks its CRM's defaults,
  // as the server reads it; the rest of its rules have defaults and their
  // own warnings.
  if (c.source === 'lead_list')
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      c.leadList?.listId ?? ''
    );
  if (c.source === 'crm_duplicates') {
    return (
      !!cleanupCrmType(c) &&
      (c.objects === undefined ||
        (Array.isArray(c.objects) && c.objects.length > 0))
    );
  }
  // Job change watches the CRM's contacts rather than a condition on them, so
  // it is ready once it knows what to watch and what counts as a move.
  if (triggerKind(c) === 'job_change') {
    const jc = c.jobChange;
    if (
      !jc ||
      !['salesforce', 'hubspot'].includes(jc.crmType) ||
      !['all', 'list', 'filter', 'reports'].includes(jc.scope) ||
      (jc.freshness !== undefined &&
        !['monthly', 'biweekly'].includes(jc.freshness))
    )
      return false;
    if (!jc.watch?.company && !jc.watch?.title) return false;
    if (jc.scope === 'list' && !jc.listId) return false;
    if (jc.scope === 'filter')
      return (
        groupComplete(jc.conditions as FilterGroup | null | undefined) &&
        (jc.crmType !== 'hubspot' || fitsHubSpotJobChangeSearch(jc.conditions))
      );
    if (jc.scope === 'reports') return jobChangeReportsReady(jc);
    return true;
  }
  if (c.source === 'crm') {
    const crm = c.crm;
    if (!crm) return false;
    if (crm.crmType === 'attio')
      return (
        attioTriggerIssue(crm) === null &&
        !attioCompanyListOnLibrary(crm, agentPlay)
      );
    if (crm.crmType !== 'salesforce' && crm.crmType !== 'hubspot') return false;
    if (!crm.objectType) return false;
    if (crm.crmType === 'hubspot' && crm.objectType === 'Lead') return false;
    if (crm.conditionType === 'list_membership') {
      // L4 mirror: list mode watches person containers — Account is a silent
      // never-fires on the BE (`invalid_object`), so the FE never offers it.
      if (crm.objectType === 'Account') return false;
      // listKind is SF-only (HS is implicitly a marketing list); missing ⇒
      // 'campaign' (back-compat). Unknown kinds mirror the BE's loud
      // `invalid_config` rejection.
      if (crm.crmType === 'salesforce') {
        const kind = crm.listKind ?? 'campaign';
        if (kind !== 'campaign' && kind !== 'list_view' && kind !== 'report') {
          return false;
        }
      }
      return !!crm.listId;
    }
    if (hasCrmChangeConditions(crm.conditions)) {
      if (crmChangeConditionsIssue(crm.conditions)) return false;
      return crm.conditions!.conditions.every((condition) =>
        CRM_CHANGE_OPERATORS.has(condition.operator)
          ? condition.operator === 'changed' ||
            Object.prototype.hasOwnProperty.call(condition, 'value')
          : isConditionComplete(condition)
      );
    }
    return groupComplete(crm.conditions);
  }
  if (c.source === 'crustdata_watch') {
    return (
      !!c.watch?.toolFilters.technology.trim() &&
      hasAnyCompanyFilter({
        ...INITIAL_COMPANY_FILTERS,
        ...c.watch.companyFilters,
      })
    );
  }
  if (c.source === 'cleanlist') {
    const cl = c.cleanlist;
    if (!cl?.kind) return false;
    if (cl.kind === 'posts') {
      return (
        [
          ...(cl.postFilters?.author_urls ?? []),
          ...(cl.postFilters?.text_phrases ?? []),
        ].some((value) => value.trim()) &&
        !isDefaultFilters(
          { ...INITIAL_FILTERS, ...cl.peopleFilters },
          INITIAL_FILTERS
        )
      );
    }
    if (cl.kind === 'jobs') {
      const f = cl.jobFilters;
      return (
        !!f &&
        [
          f.job_titles,
          f.description_phrases,
          f.technologies,
          f.industries,
          f.company_sizes,
          f.countries,
        ].some((values) => values?.some((value) => value.trim()))
      );
    }
    if (cl.kind === 'people') {
      if (cl.companyFilters?.lookalikeOf !== undefined) return false;
      try {
        if (cl.peopleFilters) buildAgentPeopleFilters(cl.peopleFilters);
      } catch {
        return false;
      }
      return !isDefaultFilters(cl.peopleFilters, INITIAL_FILTERS);
    }
    try {
      if (cl.companyFilters) {
        buildAgentCompanyFilters(
          cl.companyFilters,
          agentPlay || cl.refireOnNewSignal === true
        );
        if (cl.refireOnNewSignal && !fundingFiltered(cl.companyFilters))
          return false;
      }
    } catch {
      return false;
    }
    return !isDefaultFilters(cl.companyFilters, INITIAL_COMPANY_FILTERS);
  }
  return false;
}

/** Why a node's config is incomplete, in user words — or null when it is
 *  ready. Draft-tolerant: incompleteness warns (and gates On), never blocks
 *  Save (§6.1). */
export function configIncompleteReason(
  type: CustomNodeType,
  config: CustomNode['config'],
  agentPlay = false
): string | null {
  switch (type) {
    case 'trigger':
      if (!(config as Partial<TriggerConfig>).source) {
        return 'Pick a trigger to start building.';
      }
      if ((config as Partial<TriggerConfig>).source === 'sequence')
        return 'Sequence events cannot start agent plays. Choose another trigger. Agents can add people to sequences.';
      return (
        recordWindowError(config as Partial<TriggerConfig>) ??
        (validateTriggerConfig(config as TriggerConfig, agentPlay)
          ? null
          : (attioTriggerWords(config as Partial<TriggerConfig>, agentPlay) ??
            'Finish setting up the trigger.'))
      );
    case 'enrich': {
      const c = config as EnrichConfig;
      if (c.target === 'person') {
        const p = c.person;
        if (!p || (!p.profile && !p.email && !p.phone)) {
          return 'Pick at least one thing to enrich.';
        }
      }
      return null;
    }
    case 'fireflies_notes':
    case 'fathom_notes':
    case 'gong_notes':
      if (
        type === 'gong_notes' &&
        (config as CallNotesConfig).transcript === true
      )
        return "Gong transcripts aren't available";
      return callNotesReady(config as CallNotesConfig)
        ? null
        : 'Pick the latest call or 1 to 365 days';
    case 'find_open_jobs':
    case 'find_reviews':
      return Object.keys(config).length === 0 ? null : 'Remove unused settings';
    case 'get_company':
      return ['employer', 'crm_account'].includes(
        (config as GetCompanyConfig).source
      ) && Object.keys(config).length === 1
        ? null
        : 'Pick Employer or CRM account as the company source';
    case 'research': {
      const c = config as ResearchConfig;
      if (!c.agentIds?.length) return 'Pick at least one agent.';
      // Mirrors the API's write_kind and write_options_unused.
      if (c.agentIds.includes(WRITE_SKILL_ID))
        return writeOptionsProblem(c.write) || null;
      return c.write ? "Write settings are set but Write isn't picked" : null;
    }
    case 'validate': {
      const c = config as ValidateConfig;
      return c.email || c.phone ? null : 'Pick email or phone to verify.';
    }
    case 'add_to_sequence': {
      const c = config as AddToSequenceConfig;
      if (!c.sequenceId) return sequencePickWords(c) + '.';
      const senderIssue = sequenceSenderIssue(c);
      if (senderIssue) return senderIssue;
      const values = Object.entries(c.variables ?? {});
      if (values.length > PLAY_VALUES_MAX)
        return `Add at most ${PLAY_VALUES_MAX} values`;
      for (const [key, template] of values) {
        if (!PLAY_VALUE_KEY.test(key))
          return 'Value names use lowercase letters, numbers and underscores';
        if (!template.trim()) return `Give ${key} a value`;
      }
      return null;
    }
    case 'export': {
      const c = config as ExportConfig;
      if (c.destination === 'company_list') {
        if (!agentPlay)
          return 'Adding companies to a list runs only on an agent play.';
        return c.listId ? null : 'Pick a company list';
      }
      const list = c.destination === 'lead_list';
      // The server refuses it on a play no agent owns (agent_play_only).
      if (list && !agentPlay)
        return 'Writing to the agent’s list runs only on an agent play.';
      if (list && !c.columns?.length) return 'Add at least one column.';
      // The server refuses a value, not a saved null.
      if (list && (c.mode != null || c.keyColumn != null))
        return 'A list column is set on each person’s own row.';
      if (!list && !c.spreadsheetId)
        return 'Create a Google Sheet for this step.';
      if (!list && !c.sheetName?.trim()) return 'Name the tab rows go to.';
      // The server's own checks (play_export_step.config_issues).
      const headers = (c.columns ?? []).map((column) => column.header.trim());
      if (
        c.columns &&
        (!c.columns.length ||
          c.columns.some((column) => !column.field || !column.header.trim()))
      )
        return 'Give each column a field and a header.';
      if (new Set(headers).size !== headers.length)
        return 'Give each column a different header.';
      if (c.mode === 'upsert' && !headers.includes(c.keyColumn?.trim() ?? ''))
        return 'Pick one of the step’s columns to match rows on.';
      return null;
    }
    case 'create_google_doc':
      return createGoogleDocIssue(config as CreateGoogleDocConfig);
    case 'send_mailbox_email':
      return sendMailboxEmailIssue(config as SendMailboxEmailConfig);
    case 'send_linkedin_action':
      return sendLinkedInActionIssue(config as SendLinkedInActionConfig);
    case 'crm_action': {
      const c = config as CrmActionConfig;
      if (isCrmWriteAction(c.action)) return crmWriteConfigError(c);
      const attio = attioCrmStepIssue(c);
      if (attio) return attio;
      if (
        c.objectType === 'Account' &&
        c.mappingMode === 'custom' &&
        !c.mappings?.length
      )
        return 'A custom mapping needs at least one field.';
      if (
        c.objectType !== 'Account' &&
        c.mappingMode === 'custom' &&
        !c.mappings?.some(
          (m) =>
            m.source_column === 'email' &&
            !!m.hubspot_property &&
            !m.do_not_sync
        )
      ) {
        return 'A custom mapping needs an email field.';
      }
      return null;
    }
    case 'branch': {
      const arms = (config as BranchConfig).branches ?? [];
      const bad = arms.some(
        (a) => a.conditions !== null && !branchConditionsComplete(a.conditions)
      );
      return bad ? 'Every branch path needs complete conditions.' : null;
    }
    case 'notify': {
      const c = config as NotifyConfig;
      if (c.deliver === 'list' && !c.row?.trim()) return 'Add a row.';
      if (c.channel === 'teams') {
        if (c.webhookId && c.teamsChannelId) return 'Pick one Teams channel.';
        if (c.webhookId) {
          if (c.to === 'play_owner' || c.to === 'record_owner')
            return 'Teams owner DMs need Clu in Teams.';
          if (c.approve) return 'Approve to sequence needs Clu in Teams.';
          return null;
        }
        if (c.to === 'play_owner') {
          if (c.memberId !== undefined && !c.memberId.trim())
            return 'Pick a teammate.';
          return null;
        }
        return c.teamsChannelId ? null : 'Connect Teams and pick a channel.';
      }
      if (c.to === 'play_owner') {
        if (c.channel !== undefined && c.channel !== 'slack')
          return 'Agent owner’s Clu DM needs Slack.';
        if (c.approve)
          return 'Approve to sequence needs a Slack channel or record owners.';
        if (c.memberId !== undefined && !c.memberId.trim())
          return 'Pick a teammate.';
        return null;
      }
      // Email goes to the playbook owner — no per-node config needed.
      if (c.channel === 'email') return null;
      return c.channelId ? null : 'Connect Slack and pick a channel.';
    }
    case 'approval':
      return approvalConfigError(config);
    case 'code':
      return codeConfigError(config as CodeConfig) ?? null;
    case 'run_play':
      return handOffConfigError(config);
    case 'wait':
      return waitConfigError(config as WaitConfig);
    case 'webhook':
      return webhookConfigError(config as WebhookConfig) ?? null;
    case 'find_meetings':
      return findMeetingsConfigError(config as FindMeetingsConfig);
    case 'job_history':
      return jobHistoryConfigError(config as JobHistoryConfig);
    case 'notion_add_page':
    case 'notion_update_page':
      return notionStepIssue(type, config as Partial<NotionStepConfig>);
    case 'identify_person':
      return identifyPersonConfigError(config as IdentifyPersonConfig);
    case 'match_company':
      return matchCompanyConfigError(config as MatchCompanyConfig);
    case 'count_people': {
      // The API's count_people_audience reads only the people wire, so
      // company filters would show on the step and never narrow the count.
      const c = config as Partial<CountPeopleConfig>;
      const companies = c.audienceCompanyFilters;
      if (
        companies &&
        typeof companies === 'object' &&
        hasAnyCompanyFilter({ ...INITIAL_COMPANY_FILTERS, ...companies })
      )
        return "Count people can't filter by company. Remove the company filters";
      // The wire it reads has a condition.
      return countPeopleIssue(c.audienceFilters);
    }
    case 'instantly_capacity':
      // The gate takes no settings, only the step delay every step takes.
      return Object.keys(config).every((key) => key === 'delay')
        ? null
        : 'Remove unused settings';
    case 'agent_task': {
      // The API's agent_task_credits: a whole number, 1 to 1,000.
      const credits = (config as AgentTaskConfig).maxCredits;
      return credits === undefined ||
        (Number.isInteger(credits) && credits >= 1 && credits <= 1000)
        ? null
        : 'Credits per brief are 1 to 1,000.';
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Client-side validation (mirror of BE §4 / §6.1; no org data)
// ---------------------------------------------------------------------------

const SCHEDULE_FREQUENCIES: ReadonlySet<string> = new Set([
  'continuous',
  'daily',
  'weekdays',
  'every_n_days',
  'weekly',
  'monthly',
  'quarterly',
]);

/** 24h HH:MM, matching the BE `_v_trigger` mirror exactly. */
const SCHEDULE_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function isValidTimezone(tz: string): boolean {
  if (tz === 'UTC') return true;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Mirror of the BE schedule validation (§2a item 4, C1) — WARNING severity:
 * a bad schedule blocks the On toggle but never a draft save. Returns a
 * user-words message, or null when the schedule is absent (= continuous) or
 * valid. At run time the BE skips a play whose schedule it can't read with a
 * failed run (`schedule_unreadable`), so this mirror is advisory feedback.
 */
export function validateScheduleConfig(
  schedule: TriggerSchedule | undefined,
  /** A scheduled brief's trigger, which may run once (the API's allow_once). */
  allowOnce = false
): string | null {
  if (schedule === undefined) return null; // absent = continuous
  if (typeof schedule !== 'object' || schedule === null) {
    return 'The schedule is malformed — pick a cadence again.';
  }
  const s = schedule as Partial<TriggerSchedule>;
  if (
    !s.frequency ||
    !(
      SCHEDULE_FREQUENCIES.has(s.frequency) ||
      (allowOnce && s.frequency === 'once')
    )
  ) {
    return 'Pick how often this playbook should check for new matches.';
  }
  if (s.frequency === 'continuous') {
    const n = s.everyMinutes;
    return n == null || POLL_MINUTES.some((m) => m === n)
      ? null
      : 'Pick how often this playbook should check for new matches.';
  }
  if (s.frequency === 'every_n_days') {
    const n = s.everyNDays;
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 2 || n > 30) {
      return 'Every-N-days needs a whole number between 2 and 30.';
    }
  }
  if (s.frequency === 'weekly') {
    const d = s.weekday;
    if (typeof d !== 'number' || !Number.isInteger(d) || d < 0 || d > 6) {
      return 'Weekly schedules need a day of the week.';
    }
  }
  // Monthly takes exactly one of a day (1 to 30, or 31 for the last day) or
  // the first business day, the server's `trigger_schedule_day`.
  if (s.frequency === 'monthly') {
    const d = s.dayOfMonth;
    const hasDay = d !== undefined && d !== null;
    const firstBusinessDay = s.firstBusinessDay === true;
    const dayValid =
      typeof d === 'number' && Number.isInteger(d) && d >= 1 && d <= 31;
    if (hasDay === firstBusinessDay || (hasDay && !dayValid)) {
      return 'Pick a day of the month.';
    }
  }
  if (!s.time || !SCHEDULE_TIME_RE.test(s.time)) {
    return 'Pick a time of day for the schedule.';
  }
  if (s.timezone !== undefined && !isValidTimezone(s.timezone)) {
    return 'The schedule timezone is not recognized.';
  }
  return null;
}

/**
 * All agent ids selected by Agents (research) steps STRICTLY UPSTREAM of
 * `nodeId` — the ancestor chain root→…→parent (the graph is a tree; single
 * inbound edge per node). Used by the Q9 branch mirror below and by the
 * branch config UI to offer exactly the agents a condition can reference.
 * Cycle-safe (a cycle is reported separately by `validateGraph`).
 */
export function upstreamResearchAgentIds(
  graph: CustomGraph,
  nodeId: string
): Set<string> {
  // G28: with joins, an agent counts only when every path to the step ran
  // it (the intersection over parents).
  const memo = new Map<string, Set<string>>();
  const visiting = new Set<string>();
  const before = (id: string): Set<string> => {
    const cached = memo.get(id);
    if (cached) return cached;
    if (visiting.has(id)) return new Set();
    visiting.add(id);
    const parents = graph.edges
      .filter((e) => e.target === id)
      .map((e) => graph.nodes.find((n) => n.id === e.source));
    const paths: Set<string>[] = [];
    for (const parent of parents) {
      const through = new Set<string>();
      if (
        parent &&
        parent.type !== 'get_company' &&
        parent.type !== 'find_people'
      ) {
        for (const agent of before(parent.id)) through.add(agent);
        if (parent.type === 'research') {
          for (const agent of (parent.config as ResearchConfig).agentIds ?? [])
            through.add(agent);
        }
      }
      paths.push(through);
    }
    visiting.delete(id);
    const out = new Set<string>(
      paths.length
        ? [...paths[0]].filter((agent) => paths.every((p) => p.has(agent)))
        : []
    );
    memo.set(id, out);
    return out;
  };
  return new Set(before(nodeId));
}

/**
 * The steps every route to `nodeId` runs through (G28): after a join, only
 * their outputs are on every record. Without joins, every ancestor. Cycle-safe.
 */
export function stepsOnEveryRoute(
  graph: CustomGraph,
  nodeId: string
): Set<string> {
  const memo = new Map<string, Set<string>>();
  const visiting = new Set<string>();
  const before = (id: string): Set<string> => {
    const cached = memo.get(id);
    if (cached) return cached;
    if (visiting.has(id)) return new Set();
    visiting.add(id);
    const routes = graph.edges
      .filter((e) => e.target === id)
      .map((e) => new Set([...before(e.source), e.source]));
    visiting.delete(id);
    const out = new Set<string>(
      routes.length
        ? [...routes[0]].filter((step) => routes.every((r) => r.has(step)))
        : []
    );
    memo.set(id, out);
    return out;
  };
  return new Set(before(nodeId));
}

/** The Write kinds Agents steps upstream of `nodeId` write, on the same
 *  ancestry `upstreamResearchAgentIds` walks. */
export function upstreamWriteKinds(
  graph: CustomGraph,
  nodeId: string
): Set<string> {
  // With joins, a kind counts only when every path to the step wrote it.
  const memo = new Map<string, Set<string>>();
  const visiting = new Set<string>();
  const before = (id: string): Set<string> => {
    const cached = memo.get(id);
    if (cached) return cached;
    if (visiting.has(id)) return new Set();
    visiting.add(id);
    const parents = graph.edges
      .filter((e) => e.target === id)
      .map((e) => graph.nodes.find((n) => n.id === e.source));
    const paths: Set<string>[] = [];
    for (const parent of parents) {
      const through = new Set<string>();
      if (
        parent &&
        parent.type !== 'get_company' &&
        parent.type !== 'find_people'
      ) {
        for (const kind of before(parent.id)) through.add(kind);
        if (parent.type === 'research') {
          const config = parent.config as ResearchConfig;
          if (config.agentIds?.includes('builtin:write') && config.write?.kind)
            through.add(config.write.kind);
        }
      }
      paths.push(through);
    }
    visiting.delete(id);
    const out = new Set<string>(
      paths.length
        ? [...paths[0]].filter((kind) => paths.every((p) => p.has(kind)))
        : []
    );
    memo.set(id, out);
    return out;
  };
  return new Set(before(nodeId));
}

/** Agent id referenced by an `agent.`-prefixed condition field, or null.
 *  `agent.builtin:qualification.verdict` → `builtin:qualification`;
 *  `agent.<uuid>` → the uuid (subfields are dot-separated; ids contain no
 *  dots — matching the BE resolver's parse). */
function agentIdFromField(field: unknown): string | null {
  if (typeof field !== 'string' || !field.startsWith('agent.')) return null;
  const id = field.slice('agent.'.length).split('.')[0];
  return id || null;
}

/** Trigger's output record kind (drives the kind-flow check, §5.0). A
 *  `crm_duplicates` trigger emits duplicate groups (W5). */
export function triggerOutputKind(
  config: TriggerConfig | undefined
): RecordKind {
  const c = config as Partial<TriggerConfig> | undefined;
  if (triggerKind(c) === 'job_change') return 'person';
  if (c?.source === 'crm_duplicates') return 'duplicate_group';
  if (
    c?.source === 'lead_list' ||
    c?.source === 'fireflies' ||
    c?.source === 'fathom' ||
    c?.source === 'gong' ||
    // One person a meeting's outside attendee, as the API reads it.
    c?.source === 'calendar'
  )
    return 'person';
  if (c?.source === 'google_sheet')
    return c.sheet?.recordKind === 'company' ? 'company' : 'person';
  if (c?.source === 'sequence') return 'person';
  // A Google Doc version travels as a company record with no identity; only
  // the steps in `DOC_RECORD_STEPS` take it.
  if (c?.source === 'google_doc') return 'company';
  if (c?.source === 'webhook')
    return c.webhook?.recordKind === 'company' ? 'company' : 'person';
  if (c?.source === 'play')
    return c.play?.recordKind === 'company' ? 'company' : 'person';
  if (c?.source === 'crm') {
    // A deal carries its account, so it flows on as a company and a downstream
    // Find People fans out to the buying committee behind it.
    const object = c.crm?.objectType;
    return object === 'Account' || object === 'Deal' ? 'company' : 'person';
  }
  if (c?.source === 'crustdata_watch' || c?.source === 'company_signal')
    return 'company';
  if (c?.source === 'cleanlist') {
    return c.cleanlist?.kind === 'company' || c.cleanlist?.kind === 'jobs'
      ? 'company'
      : 'person';
  }
  // Unconfigured trigger — assume company (the Ruslan shape) so downstream
  // hints don't scream before a source is even chosen.
  return 'company';
}

/** Current graph kind on the incoming route. Invalid/missing paths stay unknown. */
export function recordKindAt(
  graph: CustomGraph | null,
  nodeId: string
): RecordKind | null {
  if (!graph) return null;
  // G28: a join's arms must agree; G29: an error path carries the kind its
  // step took in.
  const memo = new Map<string, RecordKind | null>();
  const visiting = new Set<string>();
  const entering = (id: string): RecordKind | null => {
    if (memo.has(id)) return memo.get(id)!;
    if (visiting.has(id)) return null;
    visiting.add(id);
    const node = graph.nodes.find((n) => n.id === id);
    const parents = graph.edges.filter((edge) => edge.target === id);
    let kind: RecordKind | null = null;
    if (node?.type === 'trigger') {
      kind = parents.length
        ? null
        : triggerOutputKind(node.config as TriggerConfig);
    } else if (node && parents.length) {
      const kinds = parents.map((edge): RecordKind | null => {
        const parent = graph.nodes.find((n) => n.id === edge.source);
        const before = parent ? entering(parent.id) : null;
        if (!parent || !before) return null;
        if (edge.branchKey === ERROR_ARM) return before;
        if (parent.type === 'get_company') return 'company';
        if (parent.type === 'find_people') return 'person';
        return before;
      });
      kind = kinds.every((k) => k !== null && k === kinds[0]) ? kinds[0] : null;
    }
    visiting.delete(id);
    memo.set(id, kind);
    return kind;
  };
  return entering(nodeId);
}

/**
 * The object a `crmType` trigger watches when every route into `nodeId`
 * comes from it (G28: after a join each arm still carries its records). A
 * Find people step on any route brings new people instead: unknown.
 */
export function crmSourceObjectAt(
  graph: CustomGraph | null,
  nodeId: string,
  crmType: string
): string | undefined {
  if (!graph) return undefined;
  const above = new Set(ancestors(graph, nodeId));
  const steps = graph.nodes.filter((node) => above.has(node.id));
  if (steps.some((node) => node.type === 'find_people')) return undefined;
  const source = steps.find((node) => node.type === 'trigger')?.config as
    | TriggerConfig
    | undefined;
  return source?.source === 'crm' && source.crm?.crmType === crmType
    ? source.crm.objectType
    : undefined;
}

/** New owned Company Sync nodes use Account; saved configs and library defaults stay intact. */
export function defaultNewAccountSync(
  before: CustomGraph,
  after: CustomGraph,
  agentPlay: boolean
): CustomGraph {
  if (!agentPlay) return after;
  const old = new Set(before.nodes.map((node) => node.id));
  return {
    ...after,
    nodes: after.nodes.map((node) =>
      node.type === 'crm_action' &&
      (node.config as CrmActionConfig).action === 'sync' &&
      !old.has(node.id) &&
      recordKindAt(after, node.id) === 'company'
        ? {
            ...node,
            config: {
              ...(node.config as CrmActionConfig),
              objectType: 'Account',
              action: 'sync',
            },
          }
        : node
    ),
  };
}

/** Emitted kind for an insertion spot; a Find People step emits people. */
export function recordOutputKindAt(
  graph: CustomGraph,
  nodeId: string
): RecordKind | null {
  const node = graph.nodes.find((n) => n.id === nodeId);
  const kind = recordKindAt(graph, nodeId);
  if (!node || !kind) return null;
  return node.type === 'get_company'
    ? 'company'
    : node.type === 'find_people'
      ? 'person'
      : kind;
}

/** Mirrors the accepted Get company upstream CRM read contract. */
export function getCompanyCrmIssue(
  graph: CustomGraph | null,
  nodeId: string
): string | null {
  const seen = new Set<string>([nodeId]);
  let id = nodeId;
  while (graph) {
    const edges = graph.edges.filter((edge) => edge.target === id);
    if (edges.length !== 1 || seen.has(edges[0].source)) break;
    id = edges[0].source;
    seen.add(id);
    const node = graph.nodes.find((n) => n.id === id);
    if (!node) break;
    const crm =
      node.type === 'trigger' && (node.config as TriggerConfig).source === 'crm'
        ? (node.config as TriggerConfig).crm
        : node.type === 'trigger' &&
            (node.config as TriggerConfig).source === 'job_change'
          ? {
              crmType: (node.config as TriggerConfig).jobChange?.crmType,
              objectType: 'Contact',
            }
          : node.type === 'crm_action' &&
              !isCrmWriteAction((node.config as CrmActionConfig).action)
            ? (node.config as CrmActionConfig)
            : null;
    if (crm)
      return crm.crmType === 'salesforce' &&
        ['Contact', 'default'].includes(crm.objectType ?? 'default')
        ? null
        : crm.crmType === 'hubspot'
          ? 'Use Employer on HubSpot'
          : 'Get company needs an upstream Salesforce Contact';
  }
  return 'Get company needs an upstream Salesforce Contact';
}

/**
 * Structural + config pre-validation for instant builder feedback. Pure and
 * org-data-free; the BE `validate_graph` is the authoritative backstop.
 * Returns the §6.1 split: `errors` (block Save) vs `warnings` (persist; all
 * but `unwired_arm` block On).
 */
export function validateGraph(
  graph: CustomGraph,
  agentPlay = false
): GraphValidationResult {
  const errors: GraphIssue[] = [];
  const warnings: GraphIssue[] = [];
  const { nodes, edges } = graph;

  for (const node of nodes) {
    if (
      node.type === 'trigger' &&
      (node.config as Partial<TriggerConfig>).source === 'sequence'
    )
      errors.push({
        nodeId: node.id,
        code: 'sequence_agent_execution_unsupported',
        message:
          'Sequence events cannot start agent plays. Choose another trigger. Agents can add people to sequences.',
      });
  }

  // Roots.
  const roots = nodes.filter((n) => !edges.some((e) => e.target === n.id));
  if (roots.length === 0) {
    errors.push({ code: 'no_root', message: 'Graph has no trigger node.' });
  } else if (roots.length > 1) {
    errors.push({
      code: 'multiple_roots',
      message: 'Graph has more than one entry node.',
    });
  } else if (roots[0].type !== 'trigger') {
    errors.push({
      nodeId: roots[0].id,
      code: 'root_not_trigger',
      message: 'The entry node must be a Trigger.',
    });
  }

  // Single inbound edge for every non-root node.
  for (const n of nodes) {
    if (roots[0] && n.id === roots[0].id) continue;
    const inbound = edges.filter((e) => e.target === n.id).length;
    if (inbound === 0) {
      errors.push({
        nodeId: n.id,
        code: 'orphan',
        message: 'Node is not connected to the graph.',
      });
    } else if (inbound > 1 && !agentPlay) {
      // G28: branches that rejoin run only on an agent play.
      warnings.push({
        nodeId: n.id,
        code: 'agent_play_only',
        message: 'Branches that rejoin run only on an agent play.',
      });
    }
  }

  // Node cap.
  if (nodes.length > MAX_NODES) {
    errors.push({
      code: 'too_many_nodes',
      message: `A playbook may have at most ${MAX_NODES} nodes.`,
    });
  }

  // Fan-out cap: only a Branch may have more than one child (D6 mirror). A
  // step this build doesn't know has its own rules (an Approval's two arms),
  // which the server checks.
  for (const n of nodes) {
    if (isFork(n) || !isKnownNodeType(n.type)) continue;
    const out = outEdges(graph, n.id);
    const errorEdges = out.filter((e) => e.branchKey === ERROR_ARM).length;
    if (out.length - errorEdges > 1 || errorEdges > 1) {
      errors.push({
        nodeId: n.id,
        code: 'node_fanout',
        message: 'Only Branch and Approve steps can split into multiple paths.',
      });
    }
  }

  // Branch arm rules + arm/edge consistency + unwired-arm warnings.
  for (const n of nodes) {
    if (!isBranch(n)) continue;
    const arms = (n.config as BranchConfig).branches ?? [];
    if (arms.length < MIN_ARMS || arms.length > MAX_ARMS) {
      errors.push({
        nodeId: n.id,
        code: 'branch_arm_count',
        message: `A branch needs ${MIN_ARMS}–${MAX_ARMS} paths.`,
      });
    }
    const elseCount = arms.filter((a) => a.conditions === null).length;
    if (elseCount !== 1) {
      errors.push({
        nodeId: n.id,
        code: 'branch_else',
        message: 'A branch needs exactly one else path.',
      });
    } else if (arms[arms.length - 1]?.conditions !== null) {
      errors.push({
        nodeId: n.id,
        code: 'branch_else_last',
        message: 'The else path must be last.',
      });
    }
    const keys = arms.map((a) => a.key);
    if (new Set(keys).size !== keys.length) {
      errors.push({
        nodeId: n.id,
        code: 'branch_dup_keys',
        message: 'Branch path keys must be unique.',
      });
    }
    // Out-edge branchKeys must be a subset of arm keys (D4: unconnected arms
    // are LEGAL — they mean "records matching this path are dropped").
    const wired = new Set<string>();
    for (const e of edges.filter((ed) => ed.source === n.id)) {
      if (e.branchKey === undefined || !keys.includes(e.branchKey)) {
        errors.push({
          nodeId: n.id,
          code: 'branch_edge_key',
          message: 'A branch connection points to a missing path.',
        });
      } else {
        wired.add(e.branchKey);
      }
    }
    for (const arm of arms) {
      if (!wired.has(arm.key)) {
        warnings.push({
          nodeId: n.id,
          code: 'unwired_arm',
          message: `Records matching “${arm.label}” will be dropped.`,
        });
      }
    }
  }

  for (const node of nodes.filter((n) => n.type === 'approval')) {
    const outgoing = outEdges(graph, node.id);
    const keys = outgoing.map((edge) => edge.branchKey);
    if (
      keys.some((key) => key !== 'approved' && key !== 'rejected') ||
      new Set(keys).size !== keys.length
    )
      errors.push({
        nodeId: node.id,
        code: 'approval_edge_key',
        message:
          'Approve connections must use distinct Approved and Rejected paths.',
      });
    if (!keys.includes('approved'))
      warnings.push({
        nodeId: node.id,
        code: 'approval_approved_unwired',
        message: 'Add a step on the Approved path.',
      });
  }

  // Schedule validation mirror (§2a C1 — warning: blocks On, draft OK).
  for (const n of nodes) {
    if (n.type !== 'trigger') continue;
    const reason = validateScheduleConfig(
      (n.config as Partial<TriggerConfig>).schedule,
      (n.config as Partial<TriggerConfig>).source === 'schedule'
    );
    if (reason) {
      warnings.push({
        nodeId: n.id,
        code: 'invalid_schedule',
        message: reason,
      });
    }
    // Leads per period (agents plan W2): the server's two warnings, mirrored.
    warnings.push(...perPeriodIssues(n.config as Partial<TriggerConfig>, n.id));
  }

  // Composite-audience guard (finding #16 — warning: blocks On, draft OK).
  // Firmographic company filters on a Cleanlist PEOPLE trigger show in the
  // editor, but the poll consumes ONLY `peopleWireFilters` — the run would
  // silently source a much broader audience than the user configured. Until the BE supports the composite
  // people-at-companies wire, refuse to enable the mismatch.
  for (const n of nodes) {
    if (n.type !== 'trigger') continue;
    const cfg = n.config as Partial<TriggerConfig>;
    if (cfg.source !== 'cleanlist' || cfg.cleanlist?.kind !== 'people')
      continue;
    const companyFilters = cfg.cleanlist.companyFilters;
    if (companyFilters && hasAnyCompanyFilter(companyFilters)) {
      warnings.push({
        nodeId: n.id,
        code: 'composite_filters_unsupported',
        message:
          'Company filters on a people trigger are not applied at run time yet — remove them, or the playbook will find more people than the preview shows.',
      });
    }
  }

  // Owner-assignment validation mirror (§2e R7/C2 — warnings). `keep` is a
  // no-op everywhere, so only non-keep modes are checked; assignment is only
  // meaningful on `action='sync'`.
  for (const n of nodes) {
    if (n.type !== 'crm_action') continue;
    const c = n.config as CrmActionConfig;
    if (isCrmWriteAction(c.action)) {
      if (!agentPlay)
        warnings.push({
          nodeId: n.id,
          code: 'agent_play_only',
          message: 'This CRM action runs only on an agent play.',
        });
      continue;
    }
    const a = c.assignment;
    if (!a || a.mode === 'keep') continue;
    if (advancedOwner(a)) {
      if (!agentPlay)
        warnings.push({
          nodeId: n.id,
          code: 'agent_play_only',
          message: 'Advanced Sync ownership runs only on an agent play.',
        });
      if (c.action !== 'sync')
        warnings.push({
          nodeId: n.id,
          code: 'assignment_not_sync',
          message: 'Owner assignment only applies to Sync actions.',
        });
      for (const message of advancedOwnerIssues(a))
        warnings.push({ nodeId: n.id, code: 'crm_assignment', message });
      continue;
    }
    if (
      a.mode !== 'specific' &&
      a.mode !== 'round_robin_all' &&
      a.mode !== 'round_robin_selected'
    ) {
      warnings.push({
        nodeId: n.id,
        code: 'assignment_invalid',
        message: 'The owner-assignment mode is not recognized.',
      });
      continue;
    }
    if (c.action !== 'sync') {
      warnings.push({
        nodeId: n.id,
        code: 'assignment_not_sync',
        message: 'Owner assignment only applies to Sync actions.',
      });
    }
    if (a.mode === 'specific' && (a.userIds?.length ?? 0) !== 1) {
      warnings.push({
        nodeId: n.id,
        code: 'assignment_users',
        message: 'Pick exactly one owner for specific assignment.',
      });
    }
    if (a.mode === 'round_robin_selected' && !a.userIds?.length) {
      warnings.push({
        nodeId: n.id,
        code: 'assignment_users',
        message: 'Pick at least one owner to rotate across.',
      });
    }
  }

  // Agent-ancestry mirror (§2d Q9): a branch condition on an `agent.` field
  // needs an upstream Agents step that runs that agent — otherwise the arm is
  // fail-closed dead (every record drops to else/filtered at run time).
  // Blocks On, draft OK — a warning here, matching the BE's enable gate.
  for (const n of nodes) {
    if (!isBranch(n)) continue;
    const arms = (n.config as BranchConfig).branches ?? [];
    const referenced = new Set<string>();
    for (const arm of arms) {
      for (const cond of branchLeaves(arm.conditions)) {
        const agentId = agentIdFromField((cond as ConditionLike).field);
        if (agentId) referenced.add(agentId);
      }
    }
    if (referenced.size === 0) continue;
    const upstream = upstreamResearchAgentIds(graph, n.id);
    for (const agentId of referenced) {
      if (!upstream.has(agentId)) {
        warnings.push({
          nodeId: n.id,
          code: 'agent_no_upstream',
          message:
            'A branch condition uses an agent that no earlier Agents step runs — add it upstream or remove the condition.',
        });
      }
    }
  }

  // Cycle detection, branch-nesting depth, and record-kind flow — one DFS.
  const root = roots.length === 1 ? roots[0] : undefined;
  if (root && root.type === 'trigger') {
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const visiting = new Set<string>();
    const startKind = triggerOutputKind(root.config as TriggerConfig);
    let cycleReported = false;
    // G28: each (step, kind, depth) is walked once, and the kinds every arm
    // brings into a step are compared.
    const walked = new Set<string>();
    const kindsIn = new Map<string, Set<RecordKind>>();

    const walk = (
      nodeId: string,
      inputKind: RecordKind,
      branchDepth: number
    ): void => {
      if (visiting.has(nodeId)) {
        if (!cycleReported) {
          errors.push({
            nodeId,
            code: 'cycle',
            message: "A play can't loop back on itself",
          });
          cycleReported = true;
        }
        return;
      }
      const node = nodeById.get(nodeId);
      if (!node) return;
      const key = `${nodeId}|${inputKind}|${branchDepth}`;
      if (walked.has(key)) return;
      walked.add(key);
      if (node.type !== 'trigger') {
        const seen = kindsIn.get(nodeId) ?? new Set<RecordKind>();
        seen.add(inputKind);
        kindsIn.set(nodeId, seen);
      }
      visiting.add(nodeId);

      // Record-kind flow (§2.4 + D14 symmetric enrich rule). Duplicate
      // groups and the cleanup steps are checked by `cleanupShapeIssues`
      // instead, as advisory warnings with their own words (W5 10.1).
      const meta = NODE_METADATA[node.type];
      const effectiveIn =
        node.type === 'enrich'
          ? (node.config as EnrichConfig).target
          : node.type === 'crm_action'
            ? isCrmWriteAction((node.config as CrmActionConfig).action)
              ? ['add_to_campaign', 'add_contact_role'].includes(
                  (node.config as CrmActionConfig).action
                )
                ? 'person'
                : 'any'
              : (node.config as CrmActionConfig).objectType === 'Account'
                ? 'company'
                : 'person'
            : (meta?.kindIn ?? null);
      const cleanupFlow =
        inputKind === 'duplicate_group' || isCleanupStep(node.type);
      if (
        !cleanupFlow &&
        effectiveIn &&
        effectiveIn !== 'any' &&
        effectiveIn !== inputKind
      ) {
        errors.push({
          nodeId,
          code: 'kind_mismatch',
          message: `This step needs ${effectiveIn} records but receives ${inputKind}.`,
        });
      }
      let outputKind: RecordKind;
      if (node.type === 'find_people') outputKind = 'person';
      else if (!meta || meta.kindOut === 'any') outputKind = inputKind;
      else outputKind = meta.kindOut as RecordKind;

      const nextBranchDepth = isBranch(node) ? branchDepth + 1 : branchDepth;
      if (nextBranchDepth > MAX_BRANCH_DEPTH) {
        errors.push({
          nodeId,
          code: 'branch_depth',
          message: `Branch nesting exceeds ${MAX_BRANCH_DEPTH}.`,
        });
      }

      for (const e of edges.filter((ed) => ed.source === nodeId)) {
        // G29: failed records take the error path as they entered.
        walk(
          e.target,
          e.branchKey === ERROR_ARM ? inputKind : outputKind,
          nextBranchDepth
        );
      }
      visiting.delete(nodeId);
    };

    walk(root.id, startKind, 0);
    for (const [nodeId, kinds] of kindsIn) {
      if (kinds.size > 1)
        errors.push({
          nodeId,
          code: 'join_kind_mismatch',
          message: 'Steps that rejoin must get the same kind of record.',
        });
    }
  }

  // CRM cleanup (W5 2.5): the play's shape and its rules, advisory warnings
  // that gate On and Run Test. The server's validator is the authority.
  warnings.push(...cleanupIssues(graph, root));
  // A Google Doc record has no person or company identity (API mirror).
  errors.push(...googleDocRecordIssues(graph));

  // Per-node config completeness → warnings (§6.1: persist, gate On).
  for (const n of nodes) {
    if (
      n.onError !== undefined &&
      (!n.onError ||
        typeof n.onError !== 'object' ||
        Array.isArray(n.onError) ||
        !Number.isInteger(n.onError.retries ?? 0) ||
        (n.onError.retries ?? 0) < 0 ||
        (n.onError.retries ?? 0) > 3)
    ) {
      warnings.push({
        nodeId: n.id,
        code: 'step_retries',
        message: 'Retries must be 0 to 3',
      });
    }
    warnings.push(...errorPathIssues(graph, n, agentPlay));
    if (
      n.type === 'get_company' &&
      (n.config as GetCompanyConfig).source === 'crm_account'
    ) {
      const message = getCompanyCrmIssue(graph, n.id);
      if (message)
        warnings.push({
          nodeId: n.id,
          code: 'get_company_source_crm',
          message,
        });
    }
    // The API's export_company_list_kind: a company list takes companies.
    if (
      agentPlay &&
      n.type === 'export' &&
      (n.config as ExportConfig).destination === 'company_list' &&
      recordKindAt(graph, n.id) === 'person'
    )
      warnings.push({
        nodeId: n.id,
        code: 'export_company_list_kind',
        message:
          'A company list takes companies: put this step before Find People',
      });
    if (n.type === 'wait' && !edges.some((edge) => edge.source === n.id)) {
      warnings.push({
        nodeId: n.id,
        code: 'wait_next_step',
        message: 'Add a step after the wait',
      });
    }
    const reason = configIncompleteReason(n.type, n.config, agentPlay);
    if (reason) {
      warnings.push({
        nodeId: n.id,
        code: 'config_incomplete',
        message: reason,
      });
    }
  }
  // A step that reads a Sheet column or Notion property the trigger no
  // longer has would read nothing on every row.
  for (const { nodeId, field, source } of missingSheetColumnRefs(graph))
    errors.push(
      source === 'notion'
        ? {
            nodeId,
            code: 'notion_property_missing',
            message: `${field} isn't a property of the Notion trigger. Pick the field again.`,
          }
        : {
            nodeId,
            code: 'sheet_column_missing',
            message: `${field} isn't a column of the sheet. Pick the field again.`,
          }
    );

  return { ok: errors.length === 0, errors, warnings };
}

// ---------------------------------------------------------------------------
// CRM cleanup (W5 design 2.5 and 10.1)
// ---------------------------------------------------------------------------

const CLEANUP_STEP_WORDS = 'Review duplicates, Merge duplicates, Notify';

/**
 * A cleanup play's shape: under a `crm_duplicates` trigger, one line of
 * Review duplicates, then an optional Merge duplicates, then an optional
 * Notify. Anywhere else, the cleanup steps have nothing to review. Mirrors
 * the server's `cleanup_shape`, and the duplicate-group kind flow with it.
 */
function cleanupShapeIssues(
  graph: CustomGraph,
  root: CustomNode | undefined
): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const cleanup =
    root?.type === 'trigger' &&
    isCleanupTrigger(root.config as Partial<TriggerConfig>);
  if (!root || !cleanup) {
    for (const n of graph.nodes) {
      if (!isCleanupStep(n.type)) continue;
      issues.push({
        nodeId: n.id,
        code: 'cleanup_shape',
        message:
          'Review duplicates and Merge duplicates need a CRM duplicates trigger.',
      });
    }
    return issues;
  }

  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const seen = new Set<string>([root.id]);
  let current: CustomNode = root;
  // The next position in CLEANUP_LINE a step may take.
  let next = 0;
  let sawReview = false;
  for (;;) {
    const children = outEdges(graph, current.id);
    if (children.length === 0) break;
    if (children.length > 1) {
      issues.push({
        nodeId: current.id,
        code: 'cleanup_shape',
        message: 'A cleanup play runs its steps in one line.',
      });
      break;
    }
    const child = nodeById.get(children[0].target);
    if (!child || seen.has(child.id)) break;
    seen.add(child.id);
    const position = CLEANUP_LINE.indexOf(child.type);
    if (position < 0) {
      issues.push({
        nodeId: child.id,
        code: 'cleanup_shape',
        message:
          'Only Review duplicates, Merge duplicates and Notify can follow a CRM duplicates trigger.',
      });
    } else if (position === next - 1) {
      issues.push({
        nodeId: child.id,
        code: 'cleanup_shape',
        message: 'A cleanup play uses each step once.',
      });
    } else if (position < next || (position > 0 && !sawReview)) {
      issues.push({
        nodeId: child.id,
        code: 'cleanup_shape',
        message: `Keep the steps in this order: ${CLEANUP_STEP_WORDS}.`,
      });
    } else {
      next = position + 1;
      if (position === 0) sawReview = true;
    }
    current = child;
  }
  if (!sawReview) {
    issues.push({
      nodeId: root.id,
      code: 'cleanup_shape',
      message: 'Add a Review duplicates step after the trigger.',
    });
  }
  return issues;
}

/** Every cleanup warning: the shape, the trigger's rules, and each step's. */
function cleanupIssues(
  graph: CustomGraph,
  root: CustomNode | undefined
): GraphIssue[] {
  const issues = cleanupShapeIssues(graph, root);
  const hasMergeStep = graph.nodes.some((n) => n.type === 'merge_duplicates');
  const trigger = graph.nodes.find((n) => n.type === 'trigger');
  const triggerConfig = trigger?.config as Partial<TriggerConfig> | undefined;
  const crmType = isCleanupTrigger(triggerConfig)
    ? cleanupCrmType(triggerConfig)
    : undefined;
  for (const n of graph.nodes) {
    if (n.type === 'trigger') {
      const config = n.config as Partial<TriggerConfig>;
      if (isCleanupTrigger(config))
        issues.push(
          ...cleanupTriggerIssues(config, n.id),
          ...misplacedRuleIssues(config, 'trigger', n.id)
        );
    } else if (n.type === 'review_duplicates') {
      issues.push(
        ...reviewIssues(
          n.config as Partial<ReviewDuplicatesConfig>,
          n.id,
          hasMergeStep
        ),
        ...misplacedRuleIssues(n.config, 'review_duplicates', n.id)
      );
    } else if (n.type === 'merge_duplicates') {
      issues.push(
        ...mergeIssues(
          n.config as Partial<MergeDuplicatesConfig>,
          n.id,
          crmType
        ),
        ...misplacedRuleIssues(n.config, 'merge_duplicates', n.id)
      );
    }
  }
  return issues;
}

/**
 * The steps the add-step picker offers at this graph (W5 10.1). Under a
 * `crm_duplicates` trigger: Review duplicates, Merge duplicates and Notify,
 * each while the play doesn't have it yet. Under a Google Docs trigger: the
 * steps a doc record may enter (`DOC_RECORD_STEPS`). Under any other
 * trigger: every step but the two cleanup steps.
 */
export function addableStepTypes(graph: CustomGraph): CustomNodeType[] {
  const trigger = graph.nodes.find((n) => n.type === 'trigger');
  // A scheduled brief has no records: only Notify may join its Brief step
  // (the API's agent_task_next and trigger_schedule_steps).
  if ((trigger?.config as Partial<TriggerConfig>)?.source === 'schedule')
    return ['notify'];
  if (isGoogleDocTrigger(trigger?.config as Partial<TriggerConfig>))
    return NODE_TYPE_ORDER.filter((type) => DOC_RECORD_STEPS.includes(type));
  if (!isCleanupTrigger(trigger?.config as Partial<TriggerConfig> | undefined))
    return NODE_TYPE_ORDER.filter((type) => !isCleanupStep(type));
  const present = new Set(graph.nodes.map((n) => n.type));
  return NODE_TYPE_ORDER.filter(
    (type) => CLEANUP_LINE.includes(type) && !present.has(type)
  );
}

/**
 * Where a new step would go: under `after`, and above `before` when it is
 * inserted on the edge between them.
 */
export interface AddSpot {
  after: string;
  before?: string;
}

/**
 * The steps the add-step picker offers at one spot. Under a `crm_duplicates`
 * trigger the line has one order (Review duplicates, then Merge duplicates,
 * then Notify), so a spot offers only the missing steps that keep it:
 * Review right under the trigger, Merge right under Review, Notify under
 * Review or Merge. A spot where none fits offers nothing, and its "+" hides.
 * Every other play takes `addableStepTypes` at every spot.
 */
export function addableStepTypesAt(
  graph: CustomGraph,
  spot: AddSpot
): CustomNodeType[] {
  const types = addableStepTypes(graph);
  const trigger = graph.nodes.find((n) => n.type === 'trigger');
  if (!isCleanupTrigger(trigger?.config as Partial<TriggerConfig> | undefined))
    return types.filter((type) =>
      type === 'find_open_jobs' || type === 'find_reviews'
        ? recordOutputKindAt(graph, spot.after) === 'company'
        : (type !== 'get_company' && type !== 'instantly_capacity') ||
          recordOutputKindAt(graph, spot.after) === 'person'
    );
  const typeOf = (id: string | undefined) =>
    id === undefined ? undefined : graph.nodes.find((n) => n.id === id)?.type;
  const after = typeOf(spot.after);
  const before = typeOf(spot.before);
  // An edge to a step the graph doesn't hold has nothing to fit above.
  if (spot.before !== undefined && before === undefined) return [];
  return types.filter((type) => cleanupStepFits(type, after, before));
}

/** Warnings that gate the On toggle / Run Test: every one but the advisory
 *  (`isAdvisoryIssue`: an unwired arm blocks nothing). */
export function blockingWarnings(result: GraphValidationResult): GraphIssue[] {
  return result.warnings.filter((w) => !isAdvisoryIssue(w));
}
